import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { api, API, downloadPdf as downloadPdfFile } from "@/lib/api";
import { uploadFileChunked } from "@/lib/chunkedUpload";
import CalificacionesDocentes from "@/components/app/CalificacionesDocentes";
import Admisiones from "@/components/app/Admisiones";
import HistorialBajas from "@/components/app/HistorialBajas";
import { hasModule } from "@/lib/modules";
import { ModuleLocked } from "@/components/app/ModuleGate";

const TAB_MODULE = { recibos: "cobranza", gastos: "cobranza", docentes: "portal_docentes", planeaciones: "planeaciones", dosificacion: "planeaciones", califdocentes: "calificaciones", calificaciones: "calificaciones", admisiones: "admisiones", horarios: "horarios", jornadas: "horarios", comedor: "comedor", aula: "aula", "aula-super": "aula", clubs: "clubs", "club-comunicados": "clubs", asistencias: "asistencia", musica: "musica" };
import { toPng } from "html-to-image";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, Legend, ResponsiveContainer, CartesianGrid } from "recharts";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import RecibosPanel from "@/components/app/RecibosPanel";
import Horarios from "@/components/app/Horarios";
import JornadasAdmin from "@/components/app/JornadasAdmin";
import Comedor from "@/components/app/Comedor";
import MusicModule from "@/components/app/MusicModule";
import SignatureUploader from "@/components/app/SignatureUploader";
import CredentialDesigner, { useCredentialExport, defaultTemplate, normLevel, withLevel } from "@/components/app/CredentialDesigner";
import { GroupAttendanceDownload } from "@/components/app/GroupAttendanceDownload";
import {
  Building2, Megaphone, FolderOpen, CalendarDays, FileCheck2, IdCard,
  Plus, Trash2, Download, Upload, AlertTriangle, TrendingUp, Clock, UserX, MessageCircle, Paperclip, Eye,
  ChevronLeft, ChevronRight, Bell, StickyNote, Camera, Users, GraduationCap, Copy, Loader2, KeyRound, Lock, Receipt, Search, FolderClosed, Pencil, Check, CheckCircle2, ChevronDown, RefreshCw, ClipboardCheck, BookOpen, X, Presentation, Award, CalendarClock, FileText, Banknote, Landmark, Image as ImageIcon, Gamepad2, Library, ExternalLink, Printer, Music, Link2, FileDown, UtensilsCrossed, Mail,
} from "lucide-react";

// Etiqueta legible de una clave de grupo compuesta "1A|Preescolar" -> "1A · Preescolar".
const groupLabel = (v) => (v && v.includes("|") ? v.split("|").join(" · ") : v || "");

const TABS = [
  { id: "tablero", label: "Tablero", icon: TrendingUp },
  { id: "comunicados", label: "Comunicados", icon: Megaphone },
  { id: "documentos", label: "Documentos", icon: FolderOpen },
  { id: "calendario", label: "Calendario", icon: CalendarDays },
  { id: "justificantes", label: "Justificantes", icon: FileCheck2 },
  { id: "credenciales", label: "Credenciales", icon: IdCard },
  { id: "recibos", label: "Recibos de pago", icon: Receipt },
  { id: "gastos", label: "Gastos internos", icon: Banknote },
  { id: "docentes", label: "Docentes", icon: GraduationCap },
  { id: "directorio", label: "Directorio", icon: Users },
  { id: "planeaciones", label: "Planeaciones", icon: FileCheck2 },
  { id: "califdocentes", label: "Calificaciones por docente", icon: GraduationCap },
  { id: "admisiones", label: "Admisiones", icon: Users },
  { id: "bajas", label: "Histórico de bajas", icon: UserX },
  { id: "dosificacion", label: "Avance de temario", icon: CalendarClock },
  { id: "horarios", label: "Horarios", icon: CalendarClock },
  { id: "jornadas", label: "Jornadas", icon: Clock },
  { id: "comedor", label: "Comedor", icon: UtensilsCrossed },
  { id: "incidencias", label: "Bitácora Digital", icon: AlertTriangle },
  { id: "aula", label: "Aula Virtual", icon: Presentation },
  { id: "aula-super", label: "Supervisión Aula", icon: Users },
  { id: "biblioteca", label: "Biblioteca", icon: Library },
  { id: "clubs", label: "Clubs", icon: Award },
];

const AUD = { all: "Todos", students: "Alumnos", teachers: "Docentes y Personal" };
const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

function Card({ children, accent = "bg-navy", testid }) {
  return (
    <div className="bg-white border border-nardo-200 rounded-md p-6 relative" data-testid={testid}>
      <div className={`absolute top-0 left-0 h-[2px] w-16 ${accent}`} />
      {children}
    </div>
  );
}

/* ---------------- Tablero ejecutivo ---------------- */
const PAGO_PERIODS = [
  { id: "day", label: "Día" },
  { id: "week", label: "Semana" },
  { id: "biweekly", label: "Quincena" },
  { id: "month", label: "Mes" },
  { id: "year", label: "Año" },
];
const pagoMoney = (n) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function PagosControl() {
  const { user } = useAuth();
  const [period, setPeriod] = useState("month");
  const [busy, setBusy] = useState("");
  const [report, setReport] = useState(null);
  const nodeRef = useRef(null);
  const logoSrc = user?.logo_url ? `${API}${user.logo_url}` : null;
  const periodLabel = PAGO_PERIODS.find((p) => p.id === period)?.label || "";

  const downloadPDF = (method) => {
    setBusy(`${method}-pdf`);
    try {
      downloadPdfFile("/reports/income-pdf", { params: { period, method, tz }, filename: `ingresos_${method}_${period}.pdf` });
    } finally { setTimeout(() => setBusy(""), 600); }
  };

  const downloadImage = async (method) => {
    setBusy(`${method}-img`);
    try {
      const { data } = await api.get("/reports/income-data", { params: { period, method, tz } });
      setReport(data);
      await new Promise((r) => setTimeout(r, 180));
      if (!nodeRef.current) throw new Error("no node");
      const png = await toPng(nodeRef.current, { pixelRatio: 2, cacheBust: true, backgroundColor: "#ffffff" });
      const a = document.createElement("a");
      a.href = png; a.download = `ingresos_${method}_${period}.png`; a.click();
      toast.success("Imagen descargada");
    } catch { toast.error("No se pudo generar la imagen"); }
    finally { setBusy(""); }
  };

  const MethodRow = ({ method, label, icon: Icon, tone }) => (
    <div className="flex flex-wrap items-center gap-2 py-2.5" data-testid={`pagos-row-${method}`}>
      <div className="flex items-center gap-2 min-w-[13rem]">
        <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${tone}`}><Icon className="w-4 h-4 text-white" /></span>
        <span className="text-sm font-semibold text-navy">{label}</span>
      </div>
      <div className="flex flex-wrap gap-2 ml-auto">
        <button onClick={() => downloadPDF(method)} disabled={!!busy} data-testid={`pagos-pdf-${method}`} className="text-xs inline-flex items-center gap-1.5 border border-navy text-navy hover:bg-navy hover:text-white px-3 py-2 rounded-lg font-semibold disabled:opacity-50 transition-colors">
          {busy === `${method}-pdf` ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} PDF
        </button>
        <button onClick={() => downloadImage(method)} disabled={!!busy} data-testid={`pagos-img-${method}`} className="text-xs inline-flex items-center gap-1.5 border border-navy text-navy hover:bg-navy hover:text-white px-3 py-2 rounded-lg font-semibold disabled:opacity-50 transition-colors">
          {busy === `${method}-img` ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />} Imagen
        </button>
      </div>
    </div>
  );

  return (
    <Card accent="bg-emerald-500" testid="pagos-control-card">
      <h3 className="font-display font-semibold text-lg text-navy mb-1 flex items-center gap-2"><Receipt className="w-4 h-4 text-emerald-600" /> Control de pagos · Descargas</h3>
      <p className="text-xs text-nardo-500 mb-4">Registro total de cada pago por método. Elige el periodo y descarga en PDF o imagen.</p>
      <div className="flex flex-wrap gap-1.5 mb-3">
        {PAGO_PERIODS.map((p) => (
          <button key={p.id} onClick={() => setPeriod(p.id)} data-testid={`pagos-period-${p.id}`} className={`text-[11px] px-3 py-1.5 rounded-full transition-colors ${period === p.id ? "bg-navy text-white" : "bg-white text-nardo-600 border border-nardo-200 hover:border-navy"}`}>{p.label}</button>
        ))}
      </div>
      <div className="divide-y divide-nardo-100">
        <MethodRow method="efectivo" label="Descarga total de efectivo" icon={Banknote} tone="bg-emerald-500" />
        <MethodRow method="transferencia" label="De transferencia" icon={Landmark} tone="bg-navy" />
      </div>

      {/* Nodo imprimible oculto para exportar a imagen */}
      <div style={{ position: "fixed", left: -99999, top: 0 }} aria-hidden>
        {report && (
          <div ref={nodeRef} style={{ width: 720, background: "#fff", padding: 32, fontFamily: "Manrope, Arial, sans-serif", color: "#112240" }} data-testid="pagos-report-node">
            <div style={{ display: "flex", alignItems: "center", gap: 14, borderBottom: "3px solid #112240", paddingBottom: 14, marginBottom: 16 }}>
              {logoSrc ? <img src={logoSrc} alt="" crossOrigin="anonymous" style={{ height: 52, width: "auto", objectFit: "contain" }} />
                : <img src="/zavia-icon.png" alt="ZAVIA" style={{ width: 46, height: 46, objectFit: "contain" }} />}
              <div>
                <div style={{ fontSize: 12, letterSpacing: 3, textTransform: "uppercase", color: "#1e3a8a" }}>ZAVIA</div>
                <div style={{ fontSize: 22, fontWeight: 800 }}>{report.school_name}</div>
              </div>
            </div>
            <div style={{ fontSize: 19, fontWeight: 800, marginBottom: 6 }}>Reporte de ingresos · {report.method_label}</div>
            <div style={{ fontSize: 13, color: "#334155", marginBottom: 2 }}>Periodo: <b>{report.period_label}</b> · {report.range_label}</div>
            <div style={{ fontSize: 13, color: "#334155", marginBottom: 14 }}>Pagos: <b>{report.count}</b> · Ingreso total: <b style={{ color: "#15803D" }}>{pagoMoney(report.total)}</b></div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, marginBottom: 16 }}>
              <thead><tr style={{ background: "#112240", color: "#fff" }}>
                <th style={{ textAlign: "left", padding: "7px 10px" }}>Concepto</th>
                <th style={{ textAlign: "center", padding: "7px 10px", width: 80 }}># pagos</th>
                <th style={{ textAlign: "right", padding: "7px 10px", width: 140 }}>Subtotal</th>
              </tr></thead>
              <tbody>
                {report.by_concept.length === 0 ? (
                  <tr><td colSpan={3} style={{ padding: 12, textAlign: "center", color: "#94a3b8" }}>Sin pagos en este periodo.</td></tr>
                ) : report.by_concept.map((c, i) => (
                  <tr key={i} style={{ background: i % 2 ? "#f6f7f8" : "#fff" }}>
                    <td style={{ padding: "6px 10px", fontWeight: 600 }}>{c.concept}</td>
                    <td style={{ padding: "6px 10px", textAlign: "center" }}>{c.count}</td>
                    <td style={{ padding: "6px 10px", textAlign: "right" }}>{pagoMoney(c.total)}</td>
                  </tr>
                ))}
                <tr style={{ background: "#EAF3EE", fontWeight: 800 }}>
                  <td style={{ padding: "7px 10px" }}>TOTAL</td>
                  <td style={{ padding: "7px 10px", textAlign: "center" }}>{report.count}</td>
                  <td style={{ padding: "7px 10px", textAlign: "right", color: "#15803D" }}>{pagoMoney(report.total)}</td>
                </tr>
              </tbody>
            </table>
            <div style={{ fontSize: 10, color: "#64748b" }}>Generado por Dirección · ZAVIA.</div>
          </div>
        )}
      </div>
    </Card>
  );
}

function Tablero() {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try { const r = await api.get("/direccion/overview", { params: { tz } }); setData(r.data); }
      catch (e) { console.error(e); }
    })();
  }, []);
  if (!data) return <div className="py-16 text-center text-nardo-500 text-sm">Cargando tablero…</div>;
  const t = data.totals;
  const kpis = [
    { label: "Asistencia alumnos", value: `${data.students?.attendance_pct ?? 0}%`, accent: "bg-emerald-500" },
    { label: "Asistencia docentes", value: `${data.teachers?.attendance_pct ?? 0}%`, accent: "bg-emerald-500" },
    { label: "Faltas (mes)", value: t.faltas, accent: "bg-red-500" },
    { label: "Retardos (mes)", value: t.retardos, accent: "bg-orange-500" },
    { label: "Salidas anticipadas", value: t.salidas_anticipadas, accent: "bg-violet-500" },
    { label: "Justificadas", value: t.justificadas, accent: "bg-slate-500" },
  ];
  return (
    <div className="space-y-4" data-testid="direccion-tablero">
      <div className="text-xs text-nardo-500">Resumen del mes {data.month}</div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {kpis.map((k) => (
          <Card key={k.label} accent={k.accent}>
            <div className="text-[10px] uppercase tracking-[0.3em] text-nardo-500">{k.label}</div>
            <div className="font-display font-extrabold text-4xl text-navy mt-2">{k.value}</div>
          </Card>
        ))}
      </div>
      <FinanceSummary />
      <FinanceTrend />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card accent="bg-red-500" testid="top-absent-card">
          <h3 className="font-display font-semibold text-lg text-navy mb-4 flex items-center gap-2"><UserX className="w-4 h-4 text-red-500" /> Más faltas este mes</h3>
          {data.top_absent.length === 0 ? <p className="text-sm text-nardo-500">Sin faltas registradas. 🎉</p> : (
            <div className="divide-y divide-nardo-100">
              {data.top_absent.map((p, i) => (
                <div key={i} className="py-2 flex items-center justify-between text-sm">
                  <span className="text-navy"><span className="text-[9px] uppercase tracking-wider text-nardo-400 mr-2">{p.kind === "teacher" ? "M" : "A"}</span>{p.name}</span>
                  <span className="font-mono text-red-600 font-semibold">{p.faltas}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card accent="bg-orange-500" testid="top-late-card">
          <h3 className="font-display font-semibold text-lg text-navy mb-4 flex items-center gap-2"><Clock className="w-4 h-4 text-orange-500" /> Más retardos este mes</h3>
          {data.top_late.length === 0 ? <p className="text-sm text-nardo-500">Sin retardos registrados.</p> : (
            <div className="divide-y divide-nardo-100">
              {data.top_late.map((p, i) => (
                <div key={i} className="py-2 flex items-center justify-between text-sm">
                  <span className="text-navy"><span className="text-[9px] uppercase tracking-wider text-nardo-400 mr-2">{p.kind === "teacher" ? "M" : "A"}</span>{p.name}</span>
                  <span className="font-mono text-orange-600 font-semibold">{p.retardos}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
      <PagosControl />
    </div>
  );
}

function FinanceSummary() {
  const nowMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };
  const [month, setMonth] = useState(nowMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get("/direccion/finance-summary", { params: { month, tz } })
      .then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [month]);
  const money = (n) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const profit = data?.profit ?? 0;
  const positive = profit >= 0;
  return (
    <Card accent="bg-navy" testid="finance-summary-card">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="font-display font-semibold text-lg text-navy flex items-center gap-2"><Landmark className="w-4 h-4 text-navy" /> Ingresos vs Gastos</h3>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="finance-month" className="border border-nardo-200 rounded-lg px-3 py-1.5 text-sm text-navy outline-none focus:border-navy" />
      </div>
      {loading || !data ? (
        <div className="py-8 text-center text-nardo-500 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Cargando…</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4" data-testid="finance-income">
            <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-700 flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5" /> Ingresos cobrados</div>
            <div className="font-display font-extrabold text-2xl text-emerald-700 mt-2 tabular-nums">{money(data.income_total)}</div>
            <div className="text-[11px] text-emerald-600/80 mt-1">{data.income_count} pago(s)</div>
          </div>
          <div className="rounded-xl border border-red-200 bg-red-50/60 p-4" data-testid="finance-expense">
            <div className="text-[10px] uppercase tracking-[0.2em] text-red-700 flex items-center gap-1"><Banknote className="w-3.5 h-3.5" /> Gastos internos</div>
            <div className="font-display font-extrabold text-2xl text-red-600 mt-2 tabular-nums">{money(data.expense_total)}</div>
            <div className="text-[11px] text-red-500/80 mt-1">{data.expense_count} gasto(s)</div>
          </div>
          <div className={`rounded-xl border p-4 ${positive ? "border-navy/20 bg-navy/5" : "border-orange-300 bg-orange-50/70"}`} data-testid="finance-profit">
            <div className={`text-[10px] uppercase tracking-[0.2em] flex items-center gap-1 ${positive ? "text-navy" : "text-orange-700"}`}><Landmark className="w-3.5 h-3.5" /> {positive ? "Utilidad del mes" : "Déficit del mes"}</div>
            <div className={`font-display font-extrabold text-2xl mt-2 tabular-nums ${positive ? "text-navy" : "text-orange-600"}`}>{money(profit)}</div>
            <div className="text-[11px] text-nardo-500 mt-1">Ingresos − Gastos</div>
          </div>
        </div>
      )}
      {!loading && data && (() => {
        const M = (m) => ({
          inc: data.income_by_method?.[m]?.total || 0, incN: data.income_by_method?.[m]?.count || 0,
          exp: data.expense_by_method?.[m]?.total || 0, expN: data.expense_by_method?.[m]?.count || 0,
        });
        const MethodCard = ({ title, icon, tone, m }) => {
          const v = M(m); const net = Math.round((v.inc - v.exp) * 100) / 100; const pos = net >= 0;
          return (
            <div className={`rounded-xl border p-4 ${tone}`} data-testid={`finance-method-${m}`}>
              <div className="text-[11px] uppercase tracking-[0.18em] text-navy font-bold flex items-center gap-1.5 mb-2">{icon} {title}</div>
              <div className="flex items-center justify-between text-sm py-1.5 border-b border-nardo-100">
                <span className="text-nardo-500 flex items-center gap-1"><TrendingUp className="w-3.5 h-3.5 text-emerald-500" /> Ingresos <span className="text-[11px] text-nardo-400">({v.incN})</span></span>
                <span className="font-semibold text-emerald-700 tabular-nums" data-testid={`finance-method-${m}-inc`}>{money(v.inc)}</span>
              </div>
              <div className="flex items-center justify-between text-sm py-1.5 border-b border-nardo-100">
                <span className="text-nardo-500 flex items-center gap-1"><Banknote className="w-3.5 h-3.5 text-red-500" /> Gastos <span className="text-[11px] text-nardo-400">({v.expN})</span></span>
                <span className="font-semibold text-red-600 tabular-nums" data-testid={`finance-method-${m}-exp`}>{money(v.exp)}</span>
              </div>
              <div className="flex items-center justify-between text-sm pt-2">
                <span className="text-navy font-bold">Saldo neto</span>
                <span className={`font-display font-extrabold text-lg tabular-nums ${pos ? "text-navy" : "text-orange-600"}`} data-testid={`finance-method-${m}-net`}>{money(net)}</span>
              </div>
            </div>
          );
        };
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3" data-testid="finance-by-method">
            <MethodCard title="Efectivo (Caja)" icon={<Banknote className="w-4 h-4 text-emerald-600" />} tone="border-emerald-200 bg-emerald-50/40" m="efectivo" />
            <MethodCard title="Transferencia" icon={<Landmark className="w-4 h-4 text-navy" />} tone="border-navy/20 bg-navy/5" m="transferencia" />
            {(data.income_by_method?.otros?.count || data.expense_by_method?.otros?.count) ? (
              <MethodCard title="Otros métodos" icon={<Banknote className="w-4 h-4 text-nardo-500" />} tone="border-nardo-200 bg-nardo-50" m="otros" />
            ) : null}
          </div>
        );
      })()}
    </Card>
  );
}

function FinanceTrend() {
  const [series, setSeries] = useState(null);
  const [mode, setMode] = useState("total");
  useEffect(() => {
    api.get("/direccion/finance-trend", { params: { months: 6, tz } })
      .then((r) => setSeries(r.data.series || [])).catch(() => setSeries([]));
  }, []);
  const money = (n) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const hasData = series && series.some((s) => s.income || s.expense);
  return (
    <Card accent="bg-emerald-500" testid="finance-trend-card">
      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <h3 className="font-display font-semibold text-lg text-navy flex items-center gap-2"><TrendingUp className="w-4 h-4 text-emerald-500" /> Comparativo mensual · últimos 6 meses</h3>
        <div className="flex gap-1 bg-nardo-100 rounded-lg p-0.5">
          <button onClick={() => setMode("total")} data-testid="trend-mode-total" className={`text-xs px-3 py-1 rounded-md font-semibold transition-colors ${mode === "total" ? "bg-white text-navy shadow-sm" : "text-nardo-500"}`}>Total</button>
          <button onClick={() => setMode("metodo")} data-testid="trend-mode-metodo" className={`text-xs px-3 py-1 rounded-md font-semibold transition-colors ${mode === "metodo" ? "bg-white text-navy shadow-sm" : "text-nardo-500"}`}>Por método</button>
        </div>
      </div>
      {!series ? (
        <div className="py-10 text-center text-nardo-500 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Cargando…</div>
      ) : !hasData ? (
        <div className="py-10 text-center text-nardo-500 text-sm">Aún no hay ingresos ni gastos registrados en los últimos meses.</div>
      ) : (
        <div className="h-72 w-full" data-testid="finance-trend-chart">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={series} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
              <CartesianGrid stroke="#E5E7EB" vertical={false} />
              <XAxis dataKey="label" stroke="#7A7D80" fontSize={11} />
              <YAxis stroke="#7A7D80" fontSize={11} tickFormatter={(v) => v >= 1000 ? `$${(v / 1000).toFixed(0)}k` : `$${v}`} />
              <RTooltip contentStyle={{ border: "1px solid #C9CBCD", borderRadius: 8, fontSize: 12 }} formatter={(v, n) => [money(v), n]} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              {mode === "total" ? (
                <>
                  <Bar name="Ingresos" dataKey="income" fill="#10b981" radius={[3, 3, 0, 0]} />
                  <Bar name="Gastos" dataKey="expense" fill="#ef4444" radius={[3, 3, 0, 0]} />
                  <Bar name="Utilidad" dataKey="profit" fill="#112240" radius={[3, 3, 0, 0]} />
                </>
              ) : (
                <>
                  <Bar name="Ingreso efectivo" dataKey="income_efectivo" stackId="inc" fill="#10b981" radius={[3, 3, 0, 0]} />
                  <Bar name="Ingreso transferencia" dataKey="income_transferencia" stackId="inc" fill="#6ee7b7" radius={[3, 3, 0, 0]} />
                  <Bar name="Gasto efectivo" dataKey="expense_efectivo" stackId="exp" fill="#ef4444" radius={[3, 3, 0, 0]} />
                  <Bar name="Gasto transferencia" dataKey="expense_transferencia" stackId="exp" fill="#fca5a5" radius={[3, 3, 0, 0]} />
                </>
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}



/* ---------------- Comunicados ---------------- */
function Comunicados() {
  const { user, setUser } = useAuth();
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState("all");
  const [segLevel, setSegLevel] = useState("");
  const [segGrades, setSegGrades] = useState([]);
  const [segments, setSegments] = useState({ levels: [], grades: [] });
  const [file, setFile] = useState(null);
  const fileRef = React.useRef(null);
  const [sender, setSender] = useState("");
  const [savingSender, setSavingSender] = useState(false);
  const [sendingId, setSendingId] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [annUp, setAnnUp] = useState(0);
  const [monthFilter, setMonthFilter] = useState("all");
  const [viewSummary, setViewSummary] = useState({});
  const [viewsOpen, setViewsOpen] = useState({}); // ann_id -> rows | null
  const [waModal, setWaModal] = useState(null); // { ann, recipients } | null
  const loadViewSummary = async () => { try { const r = await api.get("/direccion/announcements/views-summary"); setViewSummary(r.data || {}); } catch {} };
  const toggleViews = async (id) => {
    if (viewsOpen[id]) { setViewsOpen((s) => ({ ...s, [id]: null })); return; }
    try { const r = await api.get(`/direccion/announcements/${id}/views`); setViewsOpen((s) => ({ ...s, [id]: r.data.rows || [] })); }
    catch { toast.error("No se pudieron cargar las confirmaciones"); }
  };

  const monthLabel = (key) => {
    const [y, m] = key.split("-");
    const d = new Date(Number(y), Number(m) - 1, 1);
    const s = d.toLocaleDateString("es-MX", { month: "long", year: "numeric" });
    return s.charAt(0).toUpperCase() + s.slice(1);
  };
  const availableMonths = useMemo(() => {
    const map = new Map();
    for (const a of items) {
      const key = (a.created_at || "").slice(0, 7);
      if (key && !map.has(key)) map.set(key, monthLabel(key));
    }
    return [...map.entries()].sort((a, b) => b[0].localeCompare(a[0])).map(([key, label]) => ({ key, label }));
  }, [items]);
  const groupedMonths = useMemo(() => {
    const filtered = monthFilter === "all" ? items : items.filter((a) => (a.created_at || "").slice(0, 7) === monthFilter);
    const groups = new Map();
    for (const a of filtered) {
      const key = (a.created_at || "").slice(0, 7) || "sin-fecha";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(a);
    }
    return [...groups.entries()].sort((a, b) => b[0].localeCompare(a[0])).map(([key, list]) => [key, monthLabel(key), list]);
  }, [items, monthFilter]);
  const [counts, setCounts] = useState({ all: 0, students: 0, teachers: 0 });
  const load = async () => { try { const r = await api.get("/direccion/announcements"); setItems(r.data); } catch {} };
  const loadCounts = async () => {
    try {
      const [st, tc] = await Promise.all([
        api.get("/students", { params: { kind: "student" } }),
        api.get("/students", { params: { kind: "teacher" } }),
      ]);
      const waOf = (arr) => {
        const set = new Set();
        for (const s of arr) for (const t of ((s.billing || {}).tutors || [])) {
          const w = (t.whatsapp || "").replace(/[^0-9]/g, "");
          if (w) set.add(w);
        }
        return set;
      };
      const sSet = waOf(st.data), tSet = waOf(tc.data);
      const allSet = new Set([...sSet, ...tSet]);
      setCounts({ all: allSet.size, students: sSet.size, teachers: tSet.size });
    } catch {}
  };
  useEffect(() => { load(); loadCounts(); loadViewSummary(); (async () => { try { const r = await api.get("/direccion/segments"); setSegments(r.data || { levels: [], grades: [] }); } catch {} })(); }, []);
  useEffect(() => { setSender(user?.sender_whatsapp || ""); }, [user?.sender_whatsapp]);
  const create = async (e) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return;
    setPublishing(true);
    try {
      const { data } = await api.post("/direccion/announcements", {
        title, body, audience,
        audience_level: audience === "teachers" ? null : (segLevel || null),
        audience_grades: audience === "teachers" ? [] : segGrades,
      });
      if (file) {
        setAnnUp(0);
        const up = await uploadFileChunked(file, { onProgress: setAnnUp });
        await api.post(`/direccion/announcements/${data.id}/attachment-ref`, { token: up.token }, { timeout: 60000 });
      }
      setTitle(""); setBody(""); setAudience("all"); setSegLevel(""); setSegGrades([]); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      toast.success("Comunicado publicado"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setPublishing(false); setAnnUp(0); }
  };
  const remove = async (id) => { if (!window.confirm("¿Eliminar comunicado?")) return; await api.delete(`/direccion/announcements/${id}`); load(); };
  const removeAttachment = async (id) => { try { await api.delete(`/direccion/announcements/${id}/attachment`); toast.success("Adjunto eliminado"); load(); } catch { toast.error("Error"); } };

  const saveSender = async () => {
    setSavingSender(true);
    try {
      const digits = (sender || "").replace(/[^0-9]/g, "");
      await api.put("/auth/profile", { sender_whatsapp: digits });
      if (setUser) setUser({ ...user, sender_whatsapp: digits || null });
      toast.success("Número de WhatsApp guardado");
    } catch { toast.error("No se pudo guardar el número"); }
    finally { setSavingSender(false); }
  };

  const buildWaMessage = (a, r) => {
    const senderDigits = (sender || user?.sender_whatsapp || "").replace(/[^0-9]/g, "");
    const signature = senderDigits ? `\n\n— ${user?.school_name || "Dirección"} · WhatsApp: ${senderDigits}` : `\n\n— ${user?.school_name || "Dirección"}`;
    const link = `${window.location.origin}/circular/${r.view_token}`;
    return `*${a.title}*\n\n${a.body}\n\n📄 Ver circular completa: ${link}${signature}`;
  };

  const openWa = (a, r) => {
    window.open(`https://wa.me/${r.whatsapp}?text=${encodeURIComponent(buildWaMessage(a, r))}`, "_blank");
    // marca como "abierto" para el registro de vistos
    loadViewSummary();
  };

  const sendWhatsapp = async (a) => {
    setSendingId(a.id);
    try {
      const { data } = await api.get(`/direccion/announcements/${a.id}/recipients`);
      const list = data.recipients || [];
      if (list.length === 0) { toast.error("No hay contactos con WhatsApp registrados para esta audiencia"); return; }
      // Abrimos una lista con TODOS los contactos; cada envío es un gesto del usuario
      // (evita que el navegador bloquee los popups y garantiza llegar a todos).
      setWaModal({ ann: a, recipients: list });
    } catch { toast.error("No se pudieron obtener los destinatarios"); }
    finally { setSendingId(null); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="direccion-comunicados">
      <Card>
        <h3 className="font-display font-semibold text-lg text-navy mb-4">Nuevo comunicado</h3>
        <div className="mb-4 bg-nardo-50 border border-nardo-200 rounded-md p-3" data-testid="sender-whatsapp-box">
          <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 flex items-center gap-1.5 mb-1"><MessageCircle className="w-3.5 h-3.5 text-emerald-600" /> WhatsApp del remitente</label>
          <div className="flex gap-2">
            <input data-testid="sender-whatsapp-input" value={sender} onChange={(e) => setSender(e.target.value)} placeholder="Ej. 5215512345678 (con lada país)" className="flex-1 border-b-2 border-nardo-200 bg-transparent py-2 outline-none focus:border-navy text-navy text-sm" />
            <button data-testid="sender-whatsapp-save" onClick={saveSender} disabled={savingSender} className="inline-flex items-center gap-1.5 border border-navy/30 text-navy hover:bg-navy hover:text-white px-3 py-1.5 rounded-md text-xs font-medium transition-colors disabled:opacity-50">{savingSender ? "…" : "Guardar"}</button>
          </div>
          <p className="text-[11px] text-nardo-400 mt-1">Se incluye como firma del aviso para que los tutores sepan a qué número responder.</p>
        </div>
        <form onSubmit={create} className="space-y-4">
          <input data-testid="ann-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Título" className="w-full border-b-2 border-nardo-200 bg-transparent py-2 outline-none focus:border-navy text-navy" />
          <textarea data-testid="ann-body" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Mensaje…" rows={4} className="w-full border border-nardo-200 rounded-md p-3 outline-none focus:border-navy text-navy text-sm" />
          <select data-testid="ann-audience" value={audience} onChange={(e) => setAudience(e.target.value)} className="w-full border border-nardo-200 rounded-md p-2 text-sm text-navy bg-white outline-none focus:border-navy">
            <option value="all">Dirigido a: Todos</option>
            <option value="students">Dirigido a: Alumnos</option>
            <option value="teachers">Dirigido a: Docentes y Personal</option>
          </select>
          {audience !== "teachers" && (
            <div className="border border-nardo-200 rounded-md p-3 space-y-3" data-testid="ann-segment-box">
              <div className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Segmentar (opcional): envía solo a un grupo escolar</div>
              {segments.levels.length > 0 && (
                <select data-testid="ann-seg-level" value={segLevel} onChange={(e) => setSegLevel(e.target.value)} className="w-full border border-nardo-200 rounded-md p-2 text-sm text-navy bg-white outline-none focus:border-navy">
                  <option value="">Todos los niveles</option>
                  {segments.levels.map((l) => <option key={l.value} value={l.value}>{l.value} ({l.count})</option>)}
                </select>
              )}
              <div>
                <div className="text-[11px] text-nardo-500 mb-1.5">Grado / grupo {segGrades.length > 0 && <span className="text-navy font-medium">· {segGrades.length} seleccionado(s)</span>}</div>
                {segments.grades.length === 0 ? (
                  <p className="text-[11px] text-nardo-400">Aún no hay grados registrados. Asigna el grado/grupo al alumno para segmentar.</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                    {segments.grades.map((g) => {
                      const on = segGrades.includes(g.value);
                      return (
                        <button
                          type="button"
                          key={g.value}
                          data-testid={`ann-seg-grade-${g.value}`}
                          onClick={() => setSegGrades((s) => on ? s.filter((x) => x !== g.value) : [...s, g.value])}
                          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-navy text-white border-navy" : "border-nardo-300 text-navy hover:border-navy"}`}
                        >
                          {g.label || g.value} <span className="opacity-60">({g.count})</span>
                        </button>
                      );
                    })}
                  </div>
                )}
                {(segLevel || segGrades.length > 0) && (
                  <button type="button" onClick={() => { setSegLevel(""); setSegGrades([]); }} data-testid="ann-seg-clear" className="mt-2 text-[11px] text-nardo-500 hover:text-navy underline">Quitar segmentación (enviar a todos)</button>
                )}
              </div>
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 flex items-center gap-1.5 mb-1"><Paperclip className="w-3.5 h-3.5" /> Adjunto (cualquier archivo: documento, imagen, audio o video · hasta 1 GB, opcional)</label>
            <input ref={fileRef} data-testid="ann-file" type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="w-full text-sm text-nardo-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border file:border-nardo-200 file:bg-nardo-50 file:text-navy file:text-xs file:cursor-pointer" />
            {file && <p className="text-[11px] text-nardo-500 mt-1 truncate">Seleccionado: {file.name}</p>}
          </div>
          <button data-testid="ann-submit" type="submit" disabled={publishing} className="inline-flex items-center gap-2 bg-navy hover:bg-navy-light text-white px-5 py-2.5 rounded-md text-sm font-medium disabled:opacity-50"><Plus className="w-4 h-4" /> {publishing ? (file && annUp > 0 && annUp < 100 ? `Subiendo… ${annUp}%` : "Publicando…") : "Publicar"}</button>
        </form>
      </Card>
      <Card accent="bg-navy-light">
        <div className="flex items-center justify-between gap-2 mb-4">
          <h3 className="font-display font-semibold text-lg text-navy">Circulares publicadas</h3>
          <select data-testid="ann-month-filter" value={monthFilter} onChange={(e) => setMonthFilter(e.target.value)} className="border border-nardo-200 rounded-md px-2 py-1.5 text-xs text-navy bg-white outline-none focus:border-navy">
            <option value="all">Todos los meses</option>
            {availableMonths.map((m) => <option key={m.key} value={m.key}>{m.label}</option>)}
          </select>
        </div>
        {items.length === 0 ? <p className="text-sm text-nardo-500">Aún no hay comunicados.</p> : (
          <div className="space-y-5 max-h-[30rem] overflow-y-auto pr-1" data-testid="ann-gallery">
            {groupedMonths.length === 0 ? <p className="text-sm text-nardo-500">No hay circulares en este mes.</p> : groupedMonths.map(([mKey, mLabel, list]) => (
              <div key={mKey} data-testid={`ann-month-${mKey}`}>
                <div className="flex items-center gap-2 mb-2">
                  <CalendarDays className="w-3.5 h-3.5 text-navy-light" />
                  <span className="text-[11px] uppercase tracking-[0.2em] text-navy font-semibold">{mLabel}</span>
                  <span className="text-[10px] text-nardo-400">· {list.length}</span>
                  <span className="flex-1 h-px bg-nardo-100" />
                </div>
                <div className="space-y-3">
                {list.map((a) => (
              <div key={a.id} className="border border-nardo-100 rounded-md p-3" data-testid="ann-item">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-medium text-navy">{a.title}</div>
                    <div className="text-sm text-nardo-600 mt-1 whitespace-pre-wrap">{a.body}</div>
                    <div className="text-[10px] uppercase tracking-wider text-nardo-400 mt-2">{AUD[a.audience] || "Todos"} · {new Date(a.created_at).toLocaleDateString()}</div>
                    {(a.audience_level || (a.audience_grades && a.audience_grades.length > 0)) && (
                      <div className="mt-1.5 flex flex-wrap gap-1" data-testid="ann-segment-tags">
                        {a.audience_level && <span className="text-[10px] bg-navy/10 text-navy px-2 py-0.5 rounded-full">{a.audience_level}</span>}
                        {(a.audience_grades || []).map((g) => <span key={g} className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">{g}</span>)}
                      </div>
                    )}
                  </div>
                  <button onClick={() => remove(a.id)} className="text-nardo-400 hover:text-destructive shrink-0" data-testid={`ann-delete-${a.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="mt-3 pt-3 border-t border-nardo-100 flex flex-wrap items-center gap-2">
                  {(() => {
                    const segmented = !!(a.audience_level || (a.audience_grades && a.audience_grades.length > 0));
                    const n = counts[a.audience] ?? counts.all;
                    const label = sendingId === a.id ? "Abriendo…" : (segmented ? "Enviar por WhatsApp" : (n === 0 ? "Sin WhatsApp registrado" : `Enviar a ${n} tutor${n === 1 ? "" : "es"}`));
                    return (
                  <button onClick={() => sendWhatsapp(a)} disabled={sendingId === a.id || (!segmented && n === 0)} className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-md text-xs font-medium transition-colors disabled:opacity-50" data-testid={`ann-whatsapp-${a.id}`}>
                    <MessageCircle className="w-3.5 h-3.5" /> {label}
                  </button>
                  ); })()}
                  {a.attachment_token && (
                    <>
                      <a href={`${API}/aviso/${a.attachment_token}/archivo`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 border border-navy/30 text-navy hover:bg-navy hover:text-white px-3 py-1.5 rounded-md text-xs font-medium transition-colors" data-testid={`ann-attachment-${a.id}`}>
                        <Paperclip className="w-3.5 h-3.5" /> <span className="max-w-[10rem] truncate">{a.attachment_name || "Adjunto"}</span>
                      </a>
                      <button onClick={() => removeAttachment(a.id)} className="text-nardo-400 hover:text-destructive" title="Quitar adjunto" data-testid={`ann-attachment-delete-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </>
                  )}
                </div>
                {viewSummary[a.id]?.sent > 0 && (
                  <div className="mt-2">
                    <button onClick={() => toggleViews(a.id)} className="inline-flex items-center gap-1.5 text-[11px] text-navy hover:underline" data-testid={`ann-views-toggle-${a.id}`}>
                      <Eye className="w-3.5 h-3.5" /> Vistos: {viewSummary[a.id].opened} de {viewSummary[a.id].sent}
                    </button>
                    {viewsOpen[a.id] && (
                      <div className="mt-2 border border-nardo-100 rounded-md divide-y divide-nardo-100 max-h-48 overflow-y-auto" data-testid={`ann-views-list-${a.id}`}>
                        {viewsOpen[a.id].map((v, idx) => (
                          <div key={idx} className="flex items-center justify-between gap-2 px-2.5 py-1.5 text-[11px]">
                            <span className="text-navy truncate">{v.name} <span className="text-nardo-400">· {v.person}</span></span>
                            {v.opened_at ? (
                              <span className="text-emerald-600 whitespace-nowrap">✓ {new Date(v.opened_at).toLocaleDateString()}</span>
                            ) : (
                              <span className="text-nardo-400 whitespace-nowrap">No leído</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
                ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Dialog open={!!waModal} onOpenChange={(o) => { if (!o) setWaModal(null); }}>
        <DialogContent data-testid="wa-recipients-modal">
          <DialogHeader>
            <DialogTitle>Enviar por WhatsApp · {waModal?.recipients?.length || 0} contacto(s)</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-nardo-600 mb-2">
            Toca <b>WhatsApp</b> en cada contacto para enviar el aviso. Se abre un chat por contacto (así llegan todos, sin que el navegador bloquee los envíos).
          </p>
          <div className="max-h-[50vh] overflow-y-auto divide-y divide-nardo-100 border border-nardo-200 rounded-md">
            {(waModal?.recipients || []).map((r) => (
              <div key={r.view_token} className="flex items-center justify-between gap-3 px-3 py-2.5" data-testid={`wa-recipient-${r.whatsapp}`}>
                <div className="min-w-0">
                  <div className="text-sm text-navy font-medium truncate">{r.name}</div>
                  <div className="text-[11px] text-nardo-500 truncate">{r.person} · {r.whatsapp}</div>
                </div>
                <button
                  onClick={() => openWa(waModal.ann, r)}
                  data-testid={`wa-send-${r.whatsapp}`}
                  className="shrink-0 inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-md text-xs font-medium transition-colors"
                >
                  <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                </button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
}

/* ---------------- Documentos ---------------- */
function Documentos() {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => { try { const r = await api.get("/direccion/documents"); setItems(r.data); } catch {} };
  useEffect(() => { load(); }, []);
  const upload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Selecciona un archivo"); return; }
    setBusy(true);
    const fd = new FormData(); fd.append("file", file); fd.append("title", title);
    try {
      await api.post("/direccion/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTitle(""); setFile(null); toast.success("Documento subido"); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al subir"); }
    finally { setBusy(false); }
  };
  const download = async (d) => {
    try {
      await downloadPdfFile(`/files/${d.file_path}`, { filename: d.filename || d.title || "documento" });
    } catch { toast.error("No se pudo descargar"); }
  };
  const remove = async (id) => { if (!window.confirm("¿Eliminar documento?")) return; await api.delete(`/direccion/documents/${id}`); load(); };
  const fmtSize = (b) => b > 1048576 ? `${(b/1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b/1024))} KB`;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="direccion-documentos">
      <Card>
        <h3 className="font-display font-semibold text-lg text-navy mb-4">Subir documento</h3>
        <form onSubmit={upload} className="space-y-4">
          <input data-testid="doc-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Nombre (ej. Reglamento 2026)" className="w-full border-b-2 border-nardo-200 bg-transparent py-2 outline-none focus:border-navy text-navy" />
          <input data-testid="doc-file" type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="block w-full text-sm text-nardo-600 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:bg-nardo-100 file:text-navy file:text-sm" />
          <button data-testid="doc-submit" type="submit" disabled={busy} className="inline-flex items-center gap-2 bg-navy hover:bg-navy-light text-white px-5 py-2.5 rounded-md text-sm font-medium disabled:opacity-50"><Upload className="w-4 h-4" /> {busy ? "Subiendo…" : "Subir"}</button>
          <p className="text-[11px] text-nardo-400">PDF, Word, Excel, imágenes. Máx. 15 MB.</p>
        </form>
      </Card>
      <Card accent="bg-navy-light">
        <h3 className="font-display font-semibold text-lg text-navy mb-4">Biblioteca</h3>
        {items.length === 0 ? <p className="text-sm text-nardo-500">Aún no hay documentos.</p> : (
          <div className="divide-y divide-nardo-100 max-h-[28rem] overflow-y-auto">
            {items.map((d) => (
              <div key={d.id} className="py-3 flex items-center justify-between gap-3" data-testid="doc-item">
                <div className="min-w-0">
                  <div className="font-medium text-navy truncate">{d.title}</div>
                  <div className="text-[11px] text-nardo-500 truncate">{d.filename} · {fmtSize(d.size || 0)}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => download(d)} className="p-2 text-nardo-500 hover:text-navy hover:bg-nardo-50 rounded-md" title="Descargar" data-testid={`doc-download-${d.id}`}><Download className="w-4 h-4" /></button>
                  <button onClick={() => remove(d.id)} className="p-2 text-nardo-400 hover:text-destructive hover:bg-nardo-50 rounded-md" title="Eliminar" data-testid={`doc-delete-${d.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

/* ---------------- Calendario / Festivos ---------------- */
const CAL_COLORS = [
  { id: "navy", label: "Azul marino", dot: "bg-navy", chip: "bg-navy/10 text-navy border-navy/20" },
  { id: "emerald", label: "Verde", dot: "bg-emerald-500", chip: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { id: "amber", label: "Ámbar", dot: "bg-amber-500", chip: "bg-amber-50 text-amber-700 border-amber-200" },
  { id: "red", label: "Rojo", dot: "bg-red-500", chip: "bg-red-50 text-red-700 border-red-200" },
  { id: "violet", label: "Morado", dot: "bg-violet-500", chip: "bg-violet-50 text-violet-700 border-violet-200" },
  { id: "sky", label: "Cian", dot: "bg-sky-500", chip: "bg-sky-50 text-sky-700 border-sky-200" },
];
const colorOf = (id) => CAL_COLORS.find((c) => c.id === id) || CAL_COLORS[0];
const MONTHS_ES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const WD_ES = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const ymd = (y, m, d) => `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

function Calendario() {
  const today = new Date();
  const todayStr = ymd(today.getFullYear(), today.getMonth(), today.getDate());
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth()); // 0-11
  const [events, setEvents] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [dialogDate, setDialogDate] = useState(null);
  // form
  const [title, setTitle] = useState("");
  const [color, setColor] = useState("navy");
  const [note, setNote] = useState("");
  const [reminder, setReminder] = useState(false);
  const [noLaborable, setNoLaborable] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const [ev, hol, rem] = await Promise.all([
        api.get("/direccion/calendar-events", { params: { year, month: month + 1 } }),
        api.get("/direccion/holidays", { params: { year } }),
        api.get("/direccion/reminders/upcoming"),
      ]);
      setEvents(ev.data); setHolidays(hol.data); setReminders(rem.data);
    } catch {}
  };
  useEffect(() => { load(); }, [year, month]); // eslint-disable-line

  const eventsByDay = useMemo(() => {
    const map = {};
    for (const e of events) (map[e.date] = map[e.date] || []).push(e);
    return map;
  }, [events]);
  const holidayByDay = useMemo(() => {
    const map = {};
    for (const h of holidays) map[h.date] = h;
    return map;
  }, [holidays]);

  const prevMonth = () => { if (month === 0) { setMonth(11); setYear(year - 1); } else setMonth(month - 1); };
  const nextMonth = () => { if (month === 11) { setMonth(0); setYear(year + 1); } else setMonth(month + 1); };
  const goToday = () => { setYear(today.getFullYear()); setMonth(today.getMonth()); };

  // build grid (Monday-first)
  const firstDow = (new Date(year, month, 1).getDay() + 6) % 7; // 0=Mon
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const openDay = (d) => {
    setDialogDate(ymd(year, month, d));
    setTitle(""); setColor("navy"); setNote(""); setReminder(false); setNoLaborable(false);
  };

  const addEvent = async () => {
    if (!title.trim()) { toast.error("Escribe un título"); return; }
    setSaving(true);
    try {
      await api.post("/direccion/calendar-events", { date: dialogDate, title, color, note, reminder, no_laborable: noLaborable });
      setTitle(""); setNote(""); setReminder(false); setNoLaborable(false);
      toast.success("Evento agregado"); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };
  const removeEvent = async (id) => {
    try { await api.delete(`/direccion/calendar-events/${id}`); toast.success("Eliminado"); await load(); } catch { toast.error("Error"); }
  };

  const dayEvents = dialogDate ? (eventsByDay[dialogDate] || []) : [];
  const dayHoliday = dialogDate ? holidayByDay[dialogDate] : null;

  return (
    <div className="space-y-4" data-testid="direccion-calendario">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card testid="cal-grid-card">
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-semibold text-lg text-navy">Calendario escolar</h3>
              <div className="flex items-center gap-1">
                <button onClick={goToday} data-testid="cal-today" className="text-[11px] uppercase tracking-wider text-navy border border-nardo-200 hover:border-navy rounded-md px-2 py-1 mr-1">Hoy</button>
                <button onClick={prevMonth} data-testid="cal-prev" className="p-1.5 text-nardo-500 hover:text-navy hover:bg-nardo-50 rounded-md"><ChevronLeft className="w-4 h-4" /></button>
                <span className="text-sm font-medium text-navy w-36 text-center" data-testid="cal-month-label">{MONTHS_ES[month]} {year}</span>
                <button onClick={nextMonth} data-testid="cal-next" className="p-1.5 text-nardo-500 hover:text-navy hover:bg-nardo-50 rounded-md"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1 mb-1">
              {WD_ES.map((w) => <div key={w} className="text-center text-[10px] uppercase tracking-wider text-nardo-400 py-1">{w}</div>)}
            </div>
            <div className="grid grid-cols-7 gap-1">
              {cells.map((d, i) => {
                if (!d) return <div key={`e${i}`} />;
                const ds = ymd(year, month, d);
                const evs = eventsByDay[ds] || [];
                const hol = holidayByDay[ds];
                const isToday = ds === todayStr;
                return (
                  <button
                    key={ds}
                    onClick={() => openDay(d)}
                    data-testid={`cal-day-${ds}`}
                    className={`min-h-[68px] text-left rounded-md border p-1.5 transition-colors hover:border-navy ${isToday ? "border-navy bg-navy/5" : "border-nardo-100"}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`text-xs font-semibold ${isToday ? "text-navy" : "text-nardo-600"}`}>{d}</span>
                      {hol && <span className="w-1.5 h-1.5 rounded-full bg-red-500" title={`No laborable: ${hol.label}`} />}
                    </div>
                    <div className="mt-1 space-y-0.5">
                      {evs.slice(0, 2).map((e) => (
                        <div key={e.id} className={`flex items-center gap-1 text-[9px] truncate ${colorOf(e.color).chip} border rounded px-1 py-0.5`}>
                          {e.reminder && <Bell className="w-2 h-2 shrink-0" />}
                          <span className="truncate">{e.title}</span>
                        </div>
                      ))}
                      {evs.length > 2 && <div className="text-[9px] text-nardo-400">+{evs.length - 2} más</div>}
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-3 mt-4 text-[10px] text-nardo-500">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> No laborable</span>
              {CAL_COLORS.map((c) => <span key={c.id} className="flex items-center gap-1"><span className={`w-2 h-2 rounded-full ${c.dot}`} /> {c.label}</span>)}
            </div>
          </div>
        </Card>

        <Card accent="bg-amber-500" testid="cal-reminders-card">
          <h3 className="font-display font-semibold text-lg text-navy mb-1 flex items-center gap-2"><Bell className="w-4 h-4 text-amber-500" /> Próximos recordatorios</h3>
          <p className="text-[11px] text-nardo-400 mb-4">Eventos marcados como recordatorio, ordenados por fecha.</p>
          {reminders.length === 0 ? <p className="text-sm text-nardo-500">Sin recordatorios próximos.</p> : (
            <div className="space-y-2 max-h-[26rem] overflow-y-auto pr-1">
              {reminders.map((e) => (
                <div key={e.id} className={`border rounded-md p-2.5 ${colorOf(e.color).chip}`} data-testid={`reminder-${e.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold truncate">{e.title}</span>
                    <span className="text-[10px] font-mono whitespace-nowrap">{e.date}</span>
                  </div>
                  {e.note && <p className="text-[11px] mt-1 opacity-80 whitespace-pre-wrap">{e.note}</p>}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Day editor dialog */}
      <Dialog open={!!dialogDate} onOpenChange={(o) => !o && setDialogDate(null)}>
        <DialogContent data-testid="cal-day-dialog">
          <DialogHeader>
            <DialogTitle>{dialogDate ? new Date(dialogDate + "T12:00:00").toLocaleDateString("es-MX", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : ""}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {dayHoliday && <div className="text-xs bg-red-50 text-red-700 border border-red-200 rounded-md px-3 py-2">Día no laborable: {dayHoliday.label}</div>}
            {dayEvents.length > 0 && (
              <div className="space-y-1.5">
                {dayEvents.map((e) => (
                  <div key={e.id} className={`flex items-start justify-between gap-2 border rounded-md p-2 ${colorOf(e.color).chip}`} data-testid={`cal-event-${e.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-medium flex items-center gap-1.5">{e.reminder && <Bell className="w-3 h-3" />} {e.title}</div>
                      {e.note && <div className="text-[11px] opacity-80 mt-0.5 whitespace-pre-wrap flex items-start gap-1"><StickyNote className="w-3 h-3 mt-0.5 shrink-0" /> {e.note}</div>}
                    </div>
                    <button onClick={() => removeEvent(e.id)} className="opacity-70 hover:opacity-100 shrink-0" data-testid={`cal-event-delete-${e.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
            <div className="border-t border-nardo-100 pt-3 space-y-3">
              <input data-testid="cal-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Título (ej. Junta de padres)" className="w-full border-b-2 border-nardo-200 bg-transparent py-2 outline-none focus:border-navy text-navy text-sm" />
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 block mb-1.5">Color</label>
                <div className="flex flex-wrap gap-2">
                  {CAL_COLORS.map((c) => (
                    <button key={c.id} type="button" onClick={() => setColor(c.id)} data-testid={`cal-color-${c.id}`} className={`w-7 h-7 rounded-full ${c.dot} transition-transform ${color === c.id ? "ring-2 ring-offset-2 ring-navy scale-110" : "opacity-70 hover:opacity-100"}`} title={c.label} />
                  ))}
                </div>
              </div>
              <textarea data-testid="cal-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nota (opcional)" rows={2} className="w-full border border-nardo-200 rounded-md p-2 outline-none focus:border-navy text-navy text-sm" />
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 text-sm text-navy"><input data-testid="cal-reminder" type="checkbox" checked={reminder} onChange={(e) => setReminder(e.target.checked)} /> Recordatorio</label>
                <label className="flex items-center gap-2 text-sm text-navy"><input data-testid="cal-nolaborable" type="checkbox" checked={noLaborable} onChange={(e) => setNoLaborable(e.target.checked)} /> Día no laborable (no cuenta faltas)</label>
              </div>
              <button data-testid="cal-add" onClick={addEvent} disabled={saving} className="w-full inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-light text-white px-5 py-2.5 rounded-md text-sm font-medium disabled:opacity-50"><Plus className="w-4 h-4" /> {saving ? "Guardando…" : "Agregar al día"}</button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Justificantes ---------------- */
function Justificantes({ people }) {
  const [items, setItems] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [date, setDate] = useState("");
  const [reason, setReason] = useState("");
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = React.useRef(null);
  const cameraRef = React.useRef(null);
  const load = async () => { try { const r = await api.get("/direccion/justifications"); setItems(r.data); } catch {} };
  useEffect(() => { load(); }, []);
  const addFiles = (list) => {
    const arr = Array.from(list || []);
    if (arr.length) setFiles((prev) => [...prev, ...arr]);
  };
  const create = async (e) => {
    e.preventDefault();
    if (!studentId || !date) { toast.error("Selecciona persona y fecha"); return; }
    setSaving(true);
    try {
      // Subir comprobantes primero y enlazarlos al justificante para poder verlos/descargarlos
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("title", reason || "Justificante");
        fd.append("source", "justificante");
        fd.append("category", "Justificantes");
        fd.append("date", date);
        const { data } = await api.post(`/students/${studentId}/files`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
        uploaded.push({ file_path: data.file_path, content_type: data.content_type, title: data.title, filename: data.filename });
      }
      await api.post("/direccion/justifications", { student_id: studentId, date, reason, attachments: uploaded });
      setDate(""); setReason(""); setFiles([]);
      if (fileRef.current) fileRef.current.value = "";
      if (cameraRef.current) cameraRef.current.value = "";
      toast.success(uploaded.length ? `Justificante registrado · ${uploaded.length} comprobante(s)` : "Justificante registrado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };
  const remove = async (id) => { if (!window.confirm("¿Eliminar justificante?")) return; await api.delete(`/direccion/justifications/${id}`); load(); };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="direccion-justificantes">
      <Card>
        <h3 className="font-display font-semibold text-lg text-navy mb-1">Justificar inasistencia</h3>
        <p className="text-sm text-nardo-500 mb-4">El día pasará de <span className="text-red-600 font-medium">No asistió</span> a <span className="text-slate-600 font-medium">Justificado</span> en reportes y fichas.</p>
        <form onSubmit={create} className="space-y-4">
          <select data-testid="jus-person" value={studentId} onChange={(e) => setStudentId(e.target.value)} className="w-full border border-nardo-200 rounded-md p-2 text-sm text-navy bg-white outline-none focus:border-navy">
            <option value="">Selecciona persona…</option>
            {people.map((p) => <option key={p.id} value={p.id}>{p.kind === "teacher" ? "👤 " : "🎓 "}{p.full_name}</option>)}
          </select>
          <input data-testid="jus-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full border-b-2 border-nardo-200 bg-transparent py-2 outline-none focus:border-navy text-navy" />
          <input data-testid="jus-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo (ej. Cita médica)" className="w-full border-b-2 border-nardo-200 bg-transparent py-2 outline-none focus:border-navy text-navy" />

          <div className="bg-nardo-50 border border-nardo-200 rounded-md p-3">
            <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 flex items-center gap-1.5 mb-2"><Paperclip className="w-3.5 h-3.5" /> Comprobantes (se guardan en el expediente)</label>
            <div className="flex flex-wrap gap-2">
              <button type="button" onClick={() => fileRef.current?.click()} data-testid="jus-add-file" className="inline-flex items-center gap-1.5 border border-navy/30 text-navy hover:bg-navy hover:text-white px-3 py-1.5 rounded-md text-xs font-medium transition-colors"><Upload className="w-3.5 h-3.5" /> Agregar archivo</button>
              <button type="button" onClick={() => cameraRef.current?.click()} data-testid="jus-take-photo" className="inline-flex items-center gap-1.5 border border-navy/30 text-navy hover:bg-navy hover:text-white px-3 py-1.5 rounded-md text-xs font-medium transition-colors"><Camera className="w-3.5 h-3.5" /> Tomar foto</button>
              <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={(e) => addFiles(e.target.files)} />
              <input ref={cameraRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => addFiles(e.target.files)} />
            </div>
            {files.length > 0 && (
              <div className="mt-2 space-y-1" data-testid="jus-files-preview">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 text-[11px] text-nardo-600 bg-white border border-nardo-100 rounded px-2 py-1">
                    <span className="truncate">{f.name}</span>
                    <button type="button" onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))} className="text-nardo-400 hover:text-destructive shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button data-testid="jus-submit" type="submit" disabled={saving} className="inline-flex items-center gap-2 bg-navy hover:bg-navy-light text-white px-5 py-2.5 rounded-md text-sm font-medium disabled:opacity-50"><Plus className="w-4 h-4" /> {saving ? "Registrando…" : "Registrar"}</button>
        </form>
      </Card>
      <Card accent="bg-slate-500">
        <h3 className="font-display font-semibold text-lg text-navy mb-4">Justificantes registrados</h3>
        {items.length === 0 ? <p className="text-sm text-nardo-500">Aún no hay justificantes.</p> : (
          <div className="divide-y divide-nardo-100 max-h-[28rem] overflow-y-auto">
            {items.map((j) => (
              <div key={j.id} className="py-3 text-sm" data-testid="jus-item">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-navy font-medium truncate">{j.student_name}</div>
                    <div className="text-[11px] text-nardo-500"><span className="font-mono">{j.date}</span> · {j.reason}</div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <a href={`${API}/direccion/justifications/${j.id}/pdf`} target="_blank" rel="noreferrer" title="Descargar PDF (expediente)" data-testid={`jus-pdf-${j.id}`} className="inline-flex items-center gap-1 text-[11px] text-navy hover:bg-nardo-50 border border-nardo-200 rounded-md px-2 py-1.5 transition-colors"><Download className="w-3.5 h-3.5" /> PDF</a>
                    <button onClick={() => remove(j.id)} className="text-nardo-400 hover:text-destructive p-1.5" data-testid={`jus-delete-${j.id}`}><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                {(j.attachments?.length > 0) && (
                  <div className="flex flex-wrap gap-2 mt-2" data-testid={`jus-attachments-${j.id}`}>
                    {j.attachments.map((a, i) => {
                      const url = `${API}/files/${a.file_path}`;
                      const isImg = (a.content_type || "").startsWith("image/");
                      return (
                        <a key={i} href={url} target="_blank" rel="noreferrer" title={a.title || a.filename || "Comprobante"} data-testid={`jus-att-${j.id}-${i}`} className="group relative block w-16 h-16 rounded-md overflow-hidden border border-nardo-200 bg-nardo-50">
                          {isImg
                            ? <img src={url} alt={a.title || "Comprobante"} className="w-full h-full object-cover group-hover:opacity-80 transition-opacity" />
                            : <span className="flex items-center justify-center w-full h-full text-[9px] text-nardo-500 font-medium">PDF</span>}
                          <span className="absolute bottom-0 right-0 bg-navy/80 text-white p-0.5 rounded-tl"><Eye className="w-3 h-3" /></span>
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

/* ---------------- Credenciales ---------------- */
function Credenciales({ people }) {
  const { user } = useAuth();
  const [kind, setKind] = useState("student");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);
  const [designer, setDesigner] = useState(false);
  const [templatesByLevel, setTemplatesByLevel] = useState({});
  const [segments, setSegments] = useState({ levels: [], grades: [] });
  const [batchGroup, setBatchGroup] = useState("");
  const [includeBack, setIncludeBack] = useState(true);
  const [pageSize, setPageSize] = useState("letter");
  const logoUrl = user?.logo_url || null;
  const schoolName = user?.school_name || "Colegio";
  const { run, runBatch, node, busy: expBusy } = useCredentialExport(schoolName, logoUrl);

  const loadTemplates = async () => {
    try { const { data } = await api.get("/direccion/credential-templates", { params: { kind } }); setTemplatesByLevel(data.templates || {}); }
    catch { setTemplatesByLevel({}); }
  };
  useEffect(() => { loadTemplates(); }, [kind]); // eslint-disable-line
  useEffect(() => { (async () => { try { const { data } = await api.get("/direccion/segments"); setSegments(data || { levels: [], grades: [] }); } catch {} })(); }, []);

  const levels = useMemo(() => (segments.levels || []).map((l) => l.value), [segments]);

  const pickTemplate = useCallback((p) => {
    if (kind === "teacher") return templatesByLevel[""] || defaultTemplate("teacher", schoolName, logoUrl);
    const lvl = normLevel(p?.level);
    const tpl = templatesByLevel[lvl] || templatesByLevel[""] || defaultTemplate("student", schoolName, logoUrl);
    return withLevel(tpl, false);
  }, [kind, templatesByLevel, schoolName, logoUrl]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return people.filter((p) => (p.kind === "teacher") === (kind === "teacher"))
      .filter((p) => !s || p.full_name.toLowerCase().includes(s) || (p.grade || "").toLowerCase().includes(s));
  }, [people, q, kind]);

  const sample = filtered[0] || { full_name: "Nombre Apellido", grade: kind === "teacher" ? "Matemáticas" : "6° A", nfc_uid: "A1B2C3", kind };

  const doBatch = async () => {
    if (!batchGroup) { toast.error("Elige un grupo para imprimir por lote"); return; }
    try {
      const { data } = await api.get("/direccion/credential-batch-people", { params: { kind: "student", group: batchGroup } });
      const ppl = data.people || [];
      if (!ppl.length) { toast.error("Ese grupo no tiene alumnos"); return; }
      await runBatch(ppl, pickTemplate, { includeBack, pageSize });
    } catch { toast.error("No se pudo cargar el grupo"); }
  };

  const batchLabel = typeof expBusy === "string" && expBusy.startsWith("batch") ? expBusy.replace("batch", "").trim() : null;

  return (
    <Card testid="direccion-credenciales">
      {node}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h3 className="font-display font-semibold text-lg text-navy">Credenciales</h3>
        <button onClick={() => setDesigner(true)} data-testid="cred-edit-open" className="inline-flex items-center gap-1.5 bg-navy hover:bg-navy-light text-white px-3 py-1.5 rounded-md text-xs font-semibold"><Pencil className="w-3.5 h-3.5" /> Editar credencial</button>
      </div>
      <p className="text-sm text-nardo-500 mb-4">Personaliza el diseño (colores, textos, fuentes, logo, foto, QR, frente y reverso) y descarga en <strong>PNG</strong> o <strong>PDF</strong>. {kind === "student" && levels.length > 0 && <>Puedes guardar una plantilla distinta <strong>por nivel</strong> (se aplica sola según el nivel del alumno).</>}</p>

      <div className="inline-flex rounded-md border border-nardo-200 overflow-hidden text-xs mb-3" data-testid="cred-kind-toggle">
        <button onClick={() => setKind("student")} data-testid="cred-kind-student" className={`px-4 py-2 font-medium ${kind === "student" ? "bg-navy text-white" : "text-nardo-600"}`}>Alumnos</button>
        <button onClick={() => setKind("teacher")} data-testid="cred-kind-teacher" className={`px-4 py-2 font-medium ${kind === "teacher" ? "bg-navy text-white" : "text-nardo-600"}`}>Docentes</button>
      </div>

      {kind === "student" && levels.length > 0 && (
        <div className="mb-4 text-[11px] text-nardo-500 flex flex-wrap gap-1.5" data-testid="cred-level-status">
          <span className="text-nardo-600 font-medium">Plantillas por nivel:</span>
          <span className={`px-2 py-0.5 rounded-full ${templatesByLevel[""] ? "bg-emerald-100 text-emerald-700" : "bg-nardo-100 text-nardo-500"}`}>General {templatesByLevel[""] ? "✓" : "(fábrica)"}</span>
          {levels.map((lv) => (
            <span key={lv} className={`px-2 py-0.5 rounded-full ${templatesByLevel[lv] ? "bg-emerald-100 text-emerald-700" : "bg-nardo-100 text-nardo-500"}`}>{lv} {templatesByLevel[lv] ? "✓" : "→ usa General"}</span>
          ))}
        </div>
      )}

      {/* Impresión por lote */}
      {kind === "student" && (
        <div className="mb-4 rounded-lg border border-nardo-200 bg-nardo-50/60 p-3" data-testid="cred-batch-panel">
          <div className="flex items-center gap-1.5 text-navy font-semibold text-sm mb-2"><Printer className="w-4 h-4" /> Impresión por lote (una hoja lista para imprimir)</div>
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-[11px] text-nardo-500">Grupo
              <select value={batchGroup} onChange={(e) => setBatchGroup(e.target.value)} data-testid="cred-batch-group" className="block mt-0.5 border border-nardo-200 rounded-md px-2 py-1.5 text-sm text-navy outline-none focus:border-navy min-w-[10rem]">
                <option value="">Elige un grupo…</option>
                {(segments.grades || []).map((g) => <option key={g.value} value={g.value}>{g.label} ({g.count})</option>)}
              </select>
            </label>
            <label className="text-[11px] text-nardo-500">Hoja
              <select value={pageSize} onChange={(e) => setPageSize(e.target.value)} data-testid="cred-batch-pagesize" className="block mt-0.5 border border-nardo-200 rounded-md px-2 py-1.5 text-sm text-navy outline-none focus:border-navy">
                <option value="letter">Carta</option>
                <option value="a4">A4</option>
              </select>
            </label>
            <label className="text-[11px] text-nardo-600 inline-flex items-center gap-1.5 pb-2">
              <input type="checkbox" checked={includeBack} onChange={(e) => setIncludeBack(e.target.checked)} data-testid="cred-batch-includeback" /> Incluir reverso
            </label>
            <button onClick={doBatch} disabled={!!expBusy || !batchGroup} data-testid="cred-batch-generate" className="inline-flex items-center gap-1.5 bg-navy hover:bg-navy-light text-white px-4 py-2 rounded-md text-xs font-semibold disabled:opacity-50">
              {batchLabel ? <><Loader2 className="w-4 h-4 animate-spin" /> {batchLabel}</> : <><Printer className="w-4 h-4" /> Generar PDF</>}
            </button>
          </div>
        </div>
      )}

      <input data-testid="cred-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre o grado…" className="w-full border border-nardo-200 rounded-md px-3 py-2 text-sm text-navy mb-4 outline-none focus:border-navy" />
      {filtered.length === 0 ? <p className="text-sm text-nardo-500">Sin {kind === "teacher" ? "docentes" : "alumnos"}.</p> : (
        <div className="divide-y divide-nardo-100 max-h-[30rem] overflow-y-auto">
          {filtered.map((p) => (
            <div key={p.id} className="py-3 flex items-center justify-between gap-3" data-testid="cred-item">
              <div className="min-w-0">
                <div className="font-medium text-navy truncate">{p.full_name}</div>
                <div className="text-[11px] text-nardo-500">{p.kind === "teacher" ? "Docente" : "Alumno"} · {p.grade || "—"}{p.level ? ` · ${p.level}` : ""} · <span className="font-mono">{p.nfc_uid || "Sin UID"}</span></div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button onClick={() => run(p, pickTemplate(p), "png")} disabled={!!expBusy} className="inline-flex items-center gap-1 border border-navy/30 text-navy hover:bg-navy hover:text-white px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors disabled:opacity-50" data-testid={`cred-png-${p.id}`}>{expBusy === p.id + "png" ? "…" : "PNG"}</button>
                <button onClick={() => run(p, pickTemplate(p), "pdf")} disabled={!!expBusy} className="inline-flex items-center gap-1 bg-navy text-white hover:bg-navy-light px-3 py-1.5 rounded-md text-xs font-semibold transition-colors disabled:opacity-50" data-testid={`cred-pdf-${p.id}`}>{expBusy === p.id + "pdf" ? "…" : <><Download className="w-3.5 h-3.5" /> PDF</>}</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {designer && (
        <CredentialDesigner kind={kind} schoolName={schoolName} logoUrl={logoUrl} samplePerson={sample}
          levels={levels} initialLevel=""
          onClose={() => { setDesigner(false); loadTemplates(); }} />
      )}
    </Card>
  );
}

/* ---------------- Recibos de pago (componente compartido) ---------------- */
const Recibos = RecibosPanel;

/* ---------------- Incidencias / Bitácora (Dirección) ---------------- */
const INC_TYPE_INFO = {
  academico: { label: "Académico", cls: "bg-amber-500" },
  conductual: { label: "Conductual", cls: "bg-indigo-500" },
  socioemocional: { label: "Socio emocional", cls: "bg-teal-500" },
  // Compatibilidad con registros antiguos:
  conducta: { label: "Conducta negativa", cls: "bg-red-500" },
  reconocimiento: { label: "Reconocimiento positivo", cls: "bg-emerald-500" },
  academica: { label: "Académica", cls: "bg-amber-500" },
  salud: { label: "Salud", cls: "bg-sky-500" },
  otro: { label: "Otro", cls: "bg-slate-500" },
};
const INC_LEVEL_LABEL = { normal: "Normal", medio: "Medio", grave: "Grave", leve: "Leve", media: "Media" };
const incLevelLabel = (v) => INC_LEVEL_LABEL[v] || (v ? String(v) : "—");

function IncidenciasDireccion() {
  const [items, setItems] = useState([]);
  const [pending, setPending] = useState(0);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [studentFilter, setStudentFilter] = useState("");
  const [range, setRange] = useState("todas");
  const [analysis, setAnalysis] = useState(null);

  const rangeDates = (r) => {
    const today = new Date();
    const iso = (d) => d.toISOString().slice(0, 10);
    if (r === "hoy") return { date_from: iso(today), date_to: iso(today) };
    if (r === "semana") { const f = new Date(today); f.setDate(f.getDate() - 6); return { date_from: iso(f), date_to: iso(today) }; }
    if (r === "mes") { const f = new Date(today.getFullYear(), today.getMonth(), 1); return { date_from: iso(f), date_to: iso(today) }; }
    if (r === "ciclo") { const y = today.getMonth() >= 7 ? today.getFullYear() : today.getFullYear() - 1; return { date_from: `${y}-08-01`, date_to: iso(today) }; }
    return {};
  };
  const rangeLabel = { hoy: "Hoy", semana: "Última semana", mes: "Este mes", ciclo: "Ciclo escolar", todas: "Todo el historial" };

  const buildParams = () => {
    const p = { ...rangeDates(range) };
    if (filter) p.status = filter;
    if (studentFilter) p.student_id = studentFilter;
    return p;
  };
  const load = async (silent) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/direccion/incidents", { params: buildParams() });
      setItems(data.incidents || []); setPending(data.pending || 0); setStudents(data.students || []);
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { load(); const t = setInterval(() => load(true), 15000); return () => clearInterval(t); }, [filter, studentFilter, range]); // eslint-disable-line

  const downloadPDF = () => {
    const stName = students.find((s) => s.id === studentFilter)?.full_name;
    const label = `${rangeLabel[range]}${stName ? ` · ${stName}` : " · Todos los alumnos"}`;
    const p = { ...rangeDates(range), label };
    if (studentFilter) p.student_id = studentFilter;
    downloadPdfFile("/direccion/incidents/pdf", { params: p, filename: `incidencias_${stName || "todos"}.pdf` });
  };

  const attend = async (id) => {
    try { await api.post(`/direccion/incidents/${id}/attend`); toast.success("Marcada como atendida · guardada en el expediente del alumno"); load(); }
    catch { toast.error("No se pudo actualizar"); }
  };
  const analyze = async (sid, name) => {
    setAnalysis({ name, text: "", loading: true });
    try { const { data } = await api.post(`/analysis/student/${sid}`); setAnalysis({ name: data.student_name || name, text: data.analysis, loading: false }); }
    catch { setAnalysis(null); toast.error("No se pudo generar el análisis"); }
  };

  return (
    <div className="space-y-6" data-testid="incidencias-direccion">
      <div className="bg-white border border-nardo-200 rounded-md">
        <div className="px-6 py-4 border-b border-nardo-200 flex flex-wrap items-center gap-3">
          <AlertTriangle className="w-4 h-4 text-navy" />
          <h2 className="font-display font-semibold text-xl text-navy">Bitácora Digital</h2>
          {pending > 0 && <span className="text-xs font-semibold text-white bg-red-500 rounded-full px-2.5 py-1" data-testid="inc-pending-badge">{pending} pendiente(s)</span>}
          <button onClick={downloadPDF} data-testid="inc-download-pdf" className="ml-auto text-xs inline-flex items-center gap-1.5 bg-navy hover:bg-navy-light text-white px-3 py-1.5 rounded-md font-medium transition-colors"><Download className="w-3.5 h-3.5" /> Descargar PDF</button>
        </div>
        <div className="px-6 pt-4 flex flex-wrap items-center gap-3">
          <select value={studentFilter} onChange={(e) => setStudentFilter(e.target.value)} data-testid="inc-student-filter" className="border border-nardo-200 rounded-md px-2 py-1.5 text-xs text-navy bg-white outline-none focus:border-navy max-w-[14rem]">
            <option value="">Todos los alumnos</option>
            {students.map((s) => <option key={s.id} value={s.id}>{s.full_name} ({s.grade})</option>)}
          </select>
          <div className="flex items-center gap-1.5 flex-wrap">
            {["hoy", "semana", "mes", "ciclo", "todas"].map((r) => (
              <button key={r} onClick={() => setRange(r)} data-testid={`inc-range-${r}`} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${range === r ? "bg-navy text-white border-navy" : "border-nardo-300 text-nardo-600 hover:border-navy"}`}>{rangeLabel[r]}</button>
            ))}
          </div>
          <div className="flex items-center gap-1.5 ml-auto">
            {[["", "Todas"], ["pendiente", "Pendientes"], ["atendida", "Atendidas"]].map(([v, l]) => (
              <button key={v} onClick={() => setFilter(v)} data-testid={`inc-filter-${v || "all"}`} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filter === v ? "bg-navy text-white border-navy" : "border-nardo-300 text-nardo-600 hover:border-navy"}`}>{l}</button>
            ))}
          </div>
        </div>
        <div className="px-6 py-5">
          <p className="text-sm text-nardo-600 mb-4">Reportes registrados por los docentes. Filtra por alumno y periodo; las atendidas quedan en el expediente del alumno.</p>
          {loading ? (
            <div className="py-10 text-center text-nardo-500 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Cargando…</div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-nardo-500 text-sm">Sin incidencias {filter === "pendiente" ? "pendientes" : ""}.</div>
          ) : (
            <div className="space-y-3">
              {items.map((it) => {
                const ti = INC_TYPE_INFO[it.type] || INC_TYPE_INFO.otro;
                return (
                  <div key={it.id} className={`rounded-xl border p-4 ${it.status === "atendida" ? "border-nardo-200 bg-nardo-50/50" : "border-red-200 bg-red-50/30"}`} data-testid={`inc-dir-${it.id}`}>
                    <div className="flex items-start gap-3">
                      <span className={`shrink-0 text-[10px] font-semibold text-white px-2 py-1 rounded-full ${ti.cls}`}>{ti.label}</span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-navy font-semibold">{it.student_name} <span className="text-[11px] text-nardo-400 font-normal">· {it.grade_label || it.grade} · {incLevelLabel(it.level)} · {(it.created_at || "").slice(0, 10)}</span></div>
                        <div className="text-sm text-nardo-600 mt-0.5">{it.description}</div>
                        {it.photo_token && <a href={`${API}/incident-photo/${it.photo_token}`} target="_blank" rel="noreferrer" className="text-[11px] text-navy underline mt-1 inline-block">Ver foto</a>}
                        <div className="text-[11px] text-nardo-400 mt-1">Por {it.created_by_name} · {it.status === "atendida" ? `Atendida por ${it.attended_by || "Dirección"}` : "Pendiente"}</div>
                      </div>
                      <div className="flex flex-col gap-1.5 shrink-0">
                        <button onClick={() => analyze(it.student_id, it.student_name)} data-testid={`inc-dir-analyze-${it.id}`} className="text-[11px] inline-flex items-center gap-1 text-navy border border-navy/30 hover:bg-navy hover:text-white rounded-lg px-2.5 py-1.5 transition-colors"><TrendingUp className="w-3.5 h-3.5" /> Análisis IA</button>
                        {it.status !== "atendida" && <button onClick={() => attend(it.id)} data-testid={`inc-dir-attend-${it.id}`} className="text-[11px] inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg px-2.5 py-1.5 transition-colors"><Check className="w-3.5 h-3.5" /> Atendida</button>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {analysis && (
        <div className="fixed inset-0 z-[85] bg-navy-ink/70 flex items-start justify-center overflow-y-auto p-4" onClick={() => setAnalysis(null)} data-testid="analysis-modal">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-nardo-200">
              <div className="font-display font-bold text-navy">Análisis del alumno · {analysis.name}</div>
              <button onClick={() => setAnalysis(null)} className="text-nardo-500 hover:text-navy p-1"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5">
              {analysis.loading ? (
                <div className="py-12 text-center text-nardo-500 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Generando análisis con IA…</div>
              ) : (
                <div className="text-sm text-navy whitespace-pre-wrap leading-relaxed" data-testid="analysis-text">{analysis.text}</div>
              )}
              <div className="text-[10px] text-nardo-400 mt-4">Generado por IA a partir de incidencias, asistencia y calificaciones. Revisa antes de compartir.</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Estado de captura de asistencia por grupo (Dirección) ---------------- */
const AST_STATUS = { presente: { label: "Presente", color: "text-emerald-600" }, ausente: { label: "Ausente", color: "text-red-600" }, retardo: { label: "Retardo", color: "text-amber-600" }, justificado: { label: "Justificado", color: "text-sky-600" } };

/* GroupAttendanceDownload vive en components/app/GroupAttendanceDownload.jsx (compartido) */


function AttendanceStatusPanel() {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [groupFilter, setGroupFilter] = useState("");

  const load = async (silent) => {
    if (!silent) setLoading(true);
    try { const { data } = await api.get("/direccion/attendance-status", { params: { date } }); setData(data); }
    catch { /* silencio en carga */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); const t = setInterval(() => load(true), 15000); return () => clearInterval(t); }, [date]); // eslint-disable-line

  const allGroups = data?.groups || [];
  const groups = groupFilter ? allGroups.filter((g) => g.grade === groupFilter) : allGroups;
  return (
    <div className="bg-white border border-nardo-200 rounded-md" data-testid="attendance-status-panel">
      <div className="px-6 py-4 border-b border-nardo-200 flex flex-wrap items-center gap-3">
        <ClipboardCheck className="w-4 h-4 text-navy" />
        <h2 className="font-display font-semibold text-xl text-navy">Listas de asistencia en línea</h2>
        {data && <span className="text-xs font-medium text-nardo-500" data-testid="astatus-summary">{data.total_captured} de {data.total_groups} grupos con lista subida</span>}
        <div className="ml-auto flex items-center gap-2">
          <select value={groupFilter} onChange={(e) => { setGroupFilter(e.target.value); setExpanded(null); }} data-testid="astatus-group-filter" className="border border-nardo-200 rounded-md p-2 text-sm text-navy bg-white outline-none focus:border-navy">
            <option value="">Mostrar todos los grupos</option>
            {allGroups.map((g) => <option key={g.grade} value={g.grade}>Grupo {g.label || g.grade}</option>)}
          </select>
          <input type="date" value={date} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setDate(e.target.value)} data-testid="astatus-date" className="border border-nardo-200 rounded-md p-2 text-sm outline-none focus:border-navy" />
          <button onClick={() => load()} data-testid="astatus-refresh" title="Actualizar" className="p-2 text-nardo-500 hover:text-navy border border-nardo-200 rounded-md"><RefreshCw className="w-4 h-4" /></button>
        </div>
      </div>
      <div className="px-6 py-5">
        <p className="text-sm text-nardo-600 mb-4">Revisa qué docentes ya subieron la lista real del día. {groupFilter ? `Mostrando solo el grupo ${groupFilter}.` : "Mostrando todos los grupos."} Se actualiza automáticamente.</p>
        {loading ? (
          <div className="py-10 text-center text-nardo-500 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Cargando…</div>
        ) : groups.length === 0 ? (
          <div className="py-10 text-center text-nardo-500 text-sm">No hay grupos con alumnos registrados.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {groups.map((g) => {
              const open = expanded === g.grade;
              return (
                <div key={g.grade} className={`rounded-xl border ${g.captured ? "border-emerald-200 bg-emerald-50/40" : "border-amber-200 bg-amber-50/40"}`} data-testid={`astatus-group-${g.grade}`}>
                  <div className="p-4">
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-display font-bold text-navy text-lg">{g.label || g.grade}</div>
                      {g.captured
                        ? <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-100 rounded-full px-2.5 py-1"><CheckCircle2 className="w-3.5 h-3.5" /> Lista subida</span>
                        : <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-100 rounded-full px-2.5 py-1"><Clock className="w-3.5 h-3.5" /> Pendiente</span>}
                    </div>
                    <div className="text-[11px] text-nardo-500 mt-1">{g.student_count} alumno(s){g.titular_name ? ` · Titular: ${g.titular_name}` : (g.assigned_docentes?.length ? ` · ${g.assigned_docentes.join(", ")}` : "")}</div>
                    {(g.captured || (g.subject_lists?.length > 0)) ? (
                      <>
                        {g.captured && (
                          <>
                            <div className="flex flex-wrap gap-1.5 mt-2 text-[10px]">
                              <span className="px-2 py-0.5 rounded-full bg-emerald-500 text-white">P: {g.counts.presente || 0}</span>
                              <span className="px-2 py-0.5 rounded-full bg-red-500 text-white">A: {g.counts.ausente || 0}</span>
                              <span className="px-2 py-0.5 rounded-full bg-amber-500 text-white">R: {g.counts.retardo || 0}</span>
                              <span className="px-2 py-0.5 rounded-full bg-sky-500 text-white">J: {g.counts.justificado || 0}</span>
                            </div>
                            <div className="text-[11px] text-nardo-500 mt-2">Titular · {g.updated_by || "docente"} · {g.updated_at ? new Date(g.updated_at).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" }) : ""}</div>
                          </>
                        )}
                        {!g.captured && g.subject_lists?.length > 0 && (
                          <div className="text-[11px] text-amber-700 mt-2">Titular pendiente · {g.subject_lists.length} maestro(s) de asignatura ya subieron su lista</div>
                        )}
                        {g.subject_lists?.length > 0 && (
                          <div className="text-[11px] text-sky-700 mt-1">+ {g.subject_lists.length} lista(s) de asignatura (independientes)</div>
                        )}
                        <button onClick={() => setExpanded(open ? null : g.grade)} data-testid={`astatus-toggle-${g.grade}`} className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-navy hover:underline">
                          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} /> {open ? "Ocultar listas" : "Ver listas"}
                        </button>
                      </>
                    ) : (
                      <div className="text-[11px] text-amber-700 mt-2">Nadie ha subido la lista de este día.</div>
                    )}
                  </div>
                  {open && (
                    <div className="border-t border-nardo-200 max-h-72 overflow-y-auto" data-testid={`astatus-list-${g.grade}`}>
                      {g.captured && (
                        <div>
                          <div className="px-4 py-1.5 bg-emerald-100 text-[10px] font-semibold text-emerald-800 uppercase tracking-wide">Titular{g.titular_name ? ` · ${g.titular_name}` : ""}</div>
                          <div className="divide-y divide-emerald-100">
                            {g.students.map((s, i) => {
                              const st = AST_STATUS[s.status];
                              return (
                                <div key={i} className="flex items-center justify-between gap-2 px-4 py-2 text-xs">
                                  <span className="text-navy truncate">{i + 1}. {s.full_name}</span>
                                  <span className={`font-semibold shrink-0 ${st ? st.color : "text-nardo-400"}`}>{st ? st.label : "Sin marcar"}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      {(g.subject_lists || []).map((sl, idx) => (
                        <div key={idx}>
                          <div className="px-4 py-1.5 bg-sky-100 text-[10px] font-semibold text-sky-800 uppercase tracking-wide">{sl.docente || "Maestro"}{sl.subject ? ` · ${sl.subject}` : ""} — P:{sl.counts.presente || 0} A:{sl.counts.ausente || 0} R:{sl.counts.retardo || 0} J:{sl.counts.justificado || 0}</div>
                          <div className="divide-y divide-nardo-100">
                            {sl.students.map((s, i) => {
                              const st = AST_STATUS[s.status];
                              return (
                                <div key={i} className="flex items-center justify-between gap-2 px-4 py-2 text-xs">
                                  <span className="text-navy truncate">{i + 1}. {s.full_name}</span>
                                  <span className={`font-semibold shrink-0 ${st ? st.color : "text-nardo-400"}`}>{st ? st.label : "Sin marcar"}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- Docentes ---------------- */
function Docentes() {
  const { user } = useAuth();
  const [docentes, setDocentes] = useState([]);
  const [meta, setMeta] = useState({ max: 250, count: 0, email_domain: "" });
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [pwd, setPwd] = useState("");
  const [creating, setCreating] = useState(false);
  const [lastCred, setLastCred] = useState(null); // {email, password}
  const [grades, setGrades] = useState([]);
  const [group, setGroup] = useState("");
  const [selGroups, setSelGroups] = useState([]);
  const [selTitular, setSelTitular] = useState([]);
  const [docenteType, setDocenteType] = useState("");
  const [edType, setEdType] = useState("");
  const [editing, setEditing] = useState(null); // docente en edición
  const [edName, setEdName] = useState("");
  const [edSubject, setEdSubject] = useState("");
  const [edGroups, setEdGroups] = useState([]);
  const [edTitular, setEdTitular] = useState([]);
  const [edPassword, setEdPassword] = useState("");
  const [edSaving, setEdSaving] = useState(false);
  const [period, setPeriod] = useState("week");
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [downloading, setDownloading] = useState(false);
  const [bolGroup, setBolGroup] = useState("");
  const [bolStudents, setBolStudents] = useState([]);
  const [bolStudent, setBolStudent] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [d, seg] = await Promise.all([api.get("/docentes"), api.get("/direccion/segments")]);
      setDocentes(d.data.docentes || []);
      setMeta({ max: d.data.max, count: d.data.count, email_domain: d.data.email_domain });
      setGrades(seg.data.grades || []);
    } catch { toast.error("No se pudo cargar"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const suggestedEmail = name.trim()
    ? `${name.trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/\s+/g, ".").replace(/[^a-z0-9.]/g, "").split(".").filter(Boolean).slice(0, 2).join(".")}@${meta.email_domain}`
    : "";

  const createDocente = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Escribe el nombre del docente"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/docentes", { name: name.trim(), email: email.trim() || null, subject: subject.trim() || null, password: pwd.trim() || null, groups: selGroups, titular_groups: selTitular, docente_type: docenteType || null });
      setLastCred({ email: data.email, password: data.password, name: data.name });
      setName(""); setEmail(""); setSubject(""); setPwd(""); setSelGroups([]); setSelTitular([]); setDocenteType("");
      toast.success("Docente creado. Copia sus credenciales.");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo crear"); }
    finally { setCreating(false); }
  };

  const openEdit = (d) => { setEditing(d); setEdName(d.name || ""); setEdSubject(d.subject || ""); setEdGroups(d.groups || []); setEdTitular(d.titular_groups || []); setEdType(d.docente_type || ""); setEdPassword(""); };
  const toggleEdGroup = (g) => setEdGroups((arr) => {
    const next = arr.includes(g) ? arr.filter((x) => x !== g) : [...arr, g];
    if (!next.includes(g)) setEdTitular((t) => t.filter((x) => x !== g));
    return next;
  });
  const toggleSelGroup = (g) => setSelGroups((arr) => {
    const next = arr.includes(g) ? arr.filter((x) => x !== g) : [...arr, g];
    if (!next.includes(g)) setSelTitular((t) => t.filter((x) => x !== g));
    return next;
  });
  const toggleSelTitular = (g) => setSelTitular((arr) => arr.includes(g) ? arr.filter((x) => x !== g) : [...arr, g]);
  const toggleEdTitular = (g) => setEdTitular((arr) => arr.includes(g) ? arr.filter((x) => x !== g) : [...arr, g]);
  const saveEdit = async () => {
    setEdSaving(true);
    try {
      const { data } = await api.put(`/docentes/${editing.user_id}`, { name: edName.trim() || null, subject: edSubject.trim(), groups: edGroups, titular_groups: edTitular, docente_type: edType || null, password: edPassword.trim() || null });
      if (data.password) { setLastCred({ email: editing.email, password: data.password, name: edName || editing.name }); toast.success("Datos y nueva contraseña guardados. Cópiala."); }
      else toast.success("Docente actualizado");
      setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo guardar"); }
    finally { setEdSaving(false); }
  };

  const removeDocente = async (d) => {
    if (!window.confirm(`¿Eliminar a ${d.name} (${d.email})? Perderá el acceso.`)) return;
    try { await api.delete(`/docentes/${d.user_id}`); toast.success("Docente eliminado"); load(); }
    catch { toast.error("No se pudo eliminar"); }
  };

  const copyCred = (c) => {
    navigator.clipboard?.writeText(`Portal de Docentes\nCorreo: ${c.email}\nContraseña: ${c.password}`);
    toast.success("Credenciales copiadas");
  };

  const downloadReport = async () => {
    setDownloading(true);
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      await downloadPdfFile("/reports/group-attendance-pdf", {
        params: { grade: group, period, month: period === "month" ? month : undefined, tz },
        filename: `asistencia_${group || "grupo"}_${period}.pdf`,
      });
    } catch { toast.error("No se pudo generar el PDF"); }
    finally { setDownloading(false); }
  };

  useEffect(() => {
    if (!bolGroup) { setBolStudents([]); setBolStudent(""); return; }
    api.get("/students", { params: { kind: "student" } })
      .then((r) => { const list = Array.isArray(r.data) ? r.data : (r.data.students || []); setBolStudents(list.filter((s) => (s.group || s.grade) === bolGroup)); })
      .catch(() => {});
    setBolStudent("");
  }, [bolGroup]);
  const bolName = bolStudents.find((s) => s.id === bolStudent)?.full_name || "alumno";
  const downloadBoleta = () => bolStudent && downloadPdfFile(`/direccion/boleta/${bolStudent}/pdf`, { filename: `boleta_${bolName}.pdf` });

  return (
    <div className="space-y-8" data-testid="docentes-section">
      {/* Estado de captura de asistencia en línea por grupo */}
      <AttendanceStatusPanel />

      {/* Reporte de asistencias grupal */}
      <div className="bg-white border border-nardo-200 rounded-md">
        <div className="px-6 py-4 border-b border-nardo-200 flex items-center gap-2">
          <Download className="w-4 h-4 text-navy" />
          <h2 className="font-display font-semibold text-xl text-navy">Reporte de asistencias por grupo</h2>
        </div>
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-nardo-600">Genera un PDF imprimible con las asistencias del grupo para el periodo seleccionado.</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Grupo / grado</label>
              <select value={group} onChange={(e) => setGroup(e.target.value)} data-testid="report-group" className="mt-1 w-full border border-nardo-200 rounded-md p-2 text-sm bg-white outline-none focus:border-navy">
                <option value="">Todos los grupos</option>
                {grades.map((g) => <option key={g.value} value={g.value}>{g.label || g.value} ({g.count})</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Periodo</label>
              <select value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="report-period" className="mt-1 w-full border border-nardo-200 rounded-md p-2 text-sm bg-white outline-none focus:border-navy">
                <option value="today">Hoy</option>
                <option value="week">Esta semana</option>
                <option value="month">Mes</option>
                <option value="cycle">Año (ciclo escolar)</option>
                <option value="year">Año calendario</option>
              </select>
            </div>
            {period === "month" && (
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Mes</label>
                <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="report-month" className="mt-1 w-full border border-nardo-200 rounded-md p-2 text-sm bg-white outline-none focus:border-navy" />
              </div>
            )}
          </div>
          <button onClick={downloadReport} disabled={downloading} data-testid="report-download" className="inline-flex items-center gap-2 bg-navy hover:bg-navy-light text-white px-4 py-2.5 rounded-md text-sm font-medium transition-colors disabled:opacity-50">
            {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Descargar PDF para imprimir
          </button>
        </div>
      </div>

      {/* Boletas por alumno */}
      <div className="bg-white border border-nardo-200 rounded-md" data-testid="direccion-boletas">
        <div className="px-6 py-4 border-b border-nardo-200 flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-navy" />
          <h2 className="font-display font-semibold text-xl text-navy">Boletas por trimestre</h2>
        </div>
        <div className="px-6 py-5 space-y-4">
          <p className="text-sm text-nardo-600">Descarga la boleta de un alumno con todas las materias, promedios y calificaciones capturadas por los docentes.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Grupo / grado</label>
              <select value={bolGroup} onChange={(e) => setBolGroup(e.target.value)} data-testid="boleta-group" className="mt-1 w-full border border-nardo-200 rounded-md p-2 text-sm bg-white outline-none focus:border-navy">
                <option value="">Selecciona un grupo</option>
                {grades.map((g) => <option key={g.value} value={g.value}>{g.label || g.value} ({g.count})</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Alumno</label>
              <select value={bolStudent} onChange={(e) => setBolStudent(e.target.value)} disabled={!bolGroup} data-testid="boleta-student" className="mt-1 w-full border border-nardo-200 rounded-md p-2 text-sm bg-white outline-none focus:border-navy disabled:opacity-50">
                <option value="">{bolGroup ? "Selecciona un alumno" : "Elige grupo primero"}</option>
                {bolStudents.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
              </select>
            </div>
          </div>
          <button onClick={downloadBoleta} disabled={!bolStudent} data-testid="boleta-download" className="inline-flex items-center gap-2 bg-navy hover:bg-navy-light text-white px-4 py-2.5 rounded-md text-sm font-medium transition-colors disabled:opacity-50">
            <Download className="w-4 h-4" /> Descargar boleta PDF
          </button>
        </div>
      </div>

      {/* Alta de docentes */}
      <div className="bg-white border border-nardo-200 rounded-md">
        <div className="px-6 py-4 border-b border-nardo-200 flex items-center gap-2 flex-wrap">
          <GraduationCap className="w-4 h-4 text-navy" />
          <h2 className="font-display font-semibold text-xl text-navy">Docentes con acceso</h2>
          <span className="ml-auto text-xs text-nardo-500" data-testid="docentes-count">{meta.count} / {meta.max}</span>
        </div>
        <div className="px-6 py-5">
          <form onSubmit={createDocente} className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end" data-testid="docente-form">
            <div className="sm:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Nombre del docente</label>
              <input value={name} onChange={(e) => setName(e.target.value)} data-testid="docente-name" className="mt-1 w-full border border-nardo-200 rounded-md p-2 text-sm outline-none focus:border-navy" placeholder="Ej. María López" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Materia / cargo (opcional)</label>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="docente-subject" className="mt-1 w-full border border-nardo-200 rounded-md p-2 text-sm outline-none focus:border-navy" placeholder="Matemáticas" />
            </div>
            <div className="sm:col-span-4">
              <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Correo institucional (opcional — si lo dejas vacío se genera automático)</label>
              <input value={email} onChange={(e) => setEmail(e.target.value)} data-testid="docente-email" className="mt-1 w-full border border-nardo-200 rounded-md p-2 text-sm font-mono outline-none focus:border-navy" placeholder={suggestedEmail || `nombre.apellido@${meta.email_domain}`} />
              {suggestedEmail && !email && <p className="text-[11px] text-nardo-400 mt-1">Se generará: <span className="font-mono text-navy">{suggestedEmail}</span></p>}
            </div>
            <div className="sm:col-span-4">
              <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Contraseña asignada (opcional — mín. 6 caracteres; si la dejas vacía se genera automática)</label>
              <input value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="docente-password" className="mt-1 w-full border border-nardo-200 rounded-md p-2 text-sm font-mono outline-none focus:border-navy" placeholder="Ej. Baile2026" autoComplete="new-password" />
            </div>
            <div className="sm:col-span-4">
              <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Grupos asignados (asistencia, boletas e incidencias)</label>
              <div className="mt-1 flex flex-wrap gap-1.5" data-testid="docente-groups-select">
                {grades.length === 0 && <span className="text-[11px] text-nardo-400">Aún no hay grados registrados en alumnos.</span>}
                {grades.map((g) => {
                  const on = selGroups.includes(g.value);
                  return <button type="button" key={g.value} onClick={() => toggleSelGroup(g.value)} data-testid={`docente-group-${g.value}`} className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-navy text-white border-navy" : "border-nardo-300 text-nardo-600 hover:border-navy"}`}>{g.label || g.value} ({g.count})</button>;
                })}
              </div>
              <p className="text-[11px] text-nardo-400 mt-1">Si no eliges ninguno, el docente podrá ver todos los grupos del colegio.</p>
              {selGroups.length > 0 && (
                <div className="mt-3">
                  <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Titular de (pasa la lista oficial del grupo)</label>
                  <div className="mt-1 flex flex-wrap gap-1.5" data-testid="docente-titular-select">
                    {selGroups.map((g) => {
                      const on = selTitular.includes(g);
                      return <button type="button" key={g} onClick={() => toggleSelTitular(g)} data-testid={`docente-titular-${g}`} className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-emerald-600 text-white border-emerald-600" : "border-nardo-300 text-nardo-600 hover:border-emerald-600"}`}>{on ? "★ " : ""}{groupLabel(g)}</button>;
                    })}
                  </div>
                  <p className="text-[11px] text-nardo-400 mt-1">Los grupos donde NO es titular, los pasa como maestro de asignatura (lista independiente).</p>
                </div>
              )}
            </div>
            <div className="sm:col-span-4">
              <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Tipo de docente</label>
              <div className="mt-1 flex flex-wrap gap-1.5" data-testid="docente-type-select">
                {[["titular", "Titular"], ["cocurricular", "Co-curricular"], ["especiales", "Clases especiales"]].map(([v, l]) => (
                  <button type="button" key={v} onClick={() => setDocenteType((c) => (c === v ? "" : v))} data-testid={`docente-type-${v}`} className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${docenteType === v ? "bg-navy text-white border-navy" : "border-nardo-300 text-nardo-600 hover:border-navy"}`}>{l}</button>
                ))}
              </div>
            </div>
            <button type="submit" disabled={creating || meta.count >= meta.max} data-testid="docente-create" className="sm:col-span-4 w-full inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-light text-white px-4 py-3 rounded-md text-sm font-semibold transition-colors disabled:opacity-50">
              {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Crear docente
            </button>
          </form>

          {lastCred && (
            <div className="mt-4 border border-emerald-200 bg-emerald-50 rounded-md p-4" data-testid="docente-credentials">
              <div className="flex items-center gap-2 text-sm text-emerald-800 font-medium mb-2"><KeyRound className="w-4 h-4" /> Credenciales de {lastCred.name} (cópialas ahora)</div>
              <div className="font-mono text-sm text-navy">Correo: {lastCred.email}</div>
              <div className="font-mono text-sm text-navy">Contraseña: {lastCred.password}</div>
              <button onClick={() => copyCred(lastCred)} data-testid="docente-copy-cred" className="mt-2 inline-flex items-center gap-1.5 text-xs text-navy border border-navy/30 hover:bg-navy hover:text-white px-2.5 py-1.5 rounded-md transition-colors"><Copy className="w-3 h-3" /> Copiar</button>
            </div>
          )}

          <div className="mt-3 flex items-center gap-2 text-[11px] text-nardo-500">
            <Lock className="w-3.5 h-3.5" /> El portal de Docentes ya está habilitado. Cada docente entra con su correo y contraseña. Las cuentas se bloquean automáticamente si se suspende o vence tu suscripción.
          </div>

          <div className="mt-5 divide-y divide-nardo-100 border border-nardo-100 rounded-md">
            {loading && <div className="px-4 py-6 text-center text-sm text-nardo-500">Cargando…</div>}
            {!loading && docentes.length === 0 && <div className="px-4 py-6 text-center text-sm text-nardo-500">Aún no hay docentes. Agrega el primero arriba.</div>}
            {docentes.map((d) => (
              <div key={d.user_id} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`docente-row-${d.user_id}`}>
                <div className="min-w-0">
                  <div className="text-sm text-navy font-medium truncate">{d.name}{d.subject ? <span className="text-nardo-400 font-normal"> · {d.subject}</span> : null}</div>
                  <div className="text-[11px] text-nardo-500 font-mono truncate">{d.email}</div>
                  <div className="flex flex-wrap gap-1 mt-1 items-center">
                    {d.docente_type && <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${d.docente_type === "titular" ? "bg-emerald-100 text-emerald-700" : d.docente_type === "cocurricular" ? "bg-violet-100 text-violet-700" : "bg-amber-100 text-amber-700"}`}>{d.docente_type === "titular" ? "Titular" : d.docente_type === "cocurricular" ? "Co-curricular" : "Clases especiales"}</span>}
                    {(d.groups || []).length ? (d.groups).map((g) => <span key={g} className="text-[10px] bg-navy/10 text-navy px-2 py-0.5 rounded-full">{d.titular_groups?.includes(g) ? "★ " : ""}{g}</span>) : <span className="text-[10px] text-nardo-400">Todos los grupos</span>}
                  </div>
                </div>
                <div className="shrink-0 flex items-center gap-1">
                  <button onClick={() => openEdit(d)} data-testid={`docente-edit-${d.user_id}`} className="text-nardo-500 hover:text-navy p-1.5 rounded hover:bg-nardo-50" title="Editar grupos / contraseña"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => removeDocente(d)} data-testid={`docente-delete-${d.user_id}`} className="text-nardo-400 hover:text-destructive p-1.5"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Dialog: editar docente */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="docente-edit-dialog">
          <DialogHeader><DialogTitle>Editar docente</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 block mb-1">Nombre</label>
                <input value={edName} onChange={(e) => setEdName(e.target.value)} data-testid="edit-docente-name" className="w-full border border-nardo-200 rounded-md p-2 text-sm outline-none focus:border-navy" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 block mb-1">Materia / cargo</label>
                <input value={edSubject} onChange={(e) => setEdSubject(e.target.value)} data-testid="edit-docente-subject" className="w-full border border-nardo-200 rounded-md p-2 text-sm outline-none focus:border-navy" placeholder="Matemáticas" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 block mb-1">Grupos asignados</label>
                <div className="flex flex-wrap gap-1.5" data-testid="edit-docente-groups">
                  {grades.map((g) => {
                    const on = edGroups.includes(g.value);
                    return <button type="button" key={g.value} onClick={() => toggleEdGroup(g.value)} data-testid={`edit-group-${g.value}`} className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-navy text-white border-navy" : "border-nardo-300 text-nardo-600 hover:border-navy"}`}>{g.label || g.value}</button>;
                  })}
                </div>
                {edGroups.length > 0 && (
                  <div className="mt-3">
                    <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 block mb-1">Titular de (lista oficial del grupo)</label>
                    <div className="flex flex-wrap gap-1.5" data-testid="edit-docente-titular">
                      {edGroups.map((g) => {
                        const on = edTitular.includes(g);
                        return <button type="button" key={g} onClick={() => toggleEdTitular(g)} data-testid={`edit-titular-${g}`} className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-emerald-600 text-white border-emerald-600" : "border-nardo-300 text-nardo-600 hover:border-emerald-600"}`}>{on ? "★ " : ""}{groupLabel(g)}</button>;
                      })}
                    </div>
                  </div>
                )}
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 block mb-1">Tipo de docente</label>
                <div className="flex flex-wrap gap-1.5" data-testid="edit-docente-type">
                  {[["titular", "Titular"], ["cocurricular", "Co-curricular"], ["especiales", "Clases especiales"]].map(([v, l]) => (
                    <button type="button" key={v} onClick={() => setEdType((c) => (c === v ? "" : v))} data-testid={`edit-type-${v}`} className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${edType === v ? "bg-navy text-white border-navy" : "border-nardo-300 text-nardo-600 hover:border-navy"}`}>{l}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 block mb-1">Nueva contraseña (opcional)</label>
                <input value={edPassword} onChange={(e) => setEdPassword(e.target.value)} data-testid="edit-docente-password" className="w-full border border-nardo-200 rounded-md p-2 text-sm font-mono outline-none focus:border-navy" placeholder="Dejar vacío para no cambiar" />
              </div>
              <button onClick={saveEdit} disabled={edSaving} data-testid="edit-docente-save" className="w-full inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-light text-white px-5 py-2.5 rounded-md text-sm font-medium disabled:opacity-50">
                {edSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Guardar cambios
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Boletas por trimestre (área administrativa) ---------------- */
function Boletas() {
  const [grades, setGrades] = useState([]);
  const [bolGroup, setBolGroup] = useState("");
  const [bolStudents, setBolStudents] = useState([]);
  const [bolStudent, setBolStudent] = useState("");
  useEffect(() => { api.get("/direccion/segments").then((r) => setGrades(r.data.grades || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (!bolGroup) { setBolStudents([]); setBolStudent(""); return; }
    api.get("/students", { params: { kind: "student" } })
      .then((r) => { const list = Array.isArray(r.data) ? r.data : (r.data.students || []); setBolStudents(list.filter((s) => (s.group || s.grade) === bolGroup)); })
      .catch(() => {});
    setBolStudent("");
  }, [bolGroup]);
  const bolName = bolStudents.find((s) => s.id === bolStudent)?.full_name || "alumno";
  const downloadBoleta = () => bolStudent && downloadPdfFile(`/direccion/boleta/${bolStudent}/pdf`, { filename: `boleta_${bolName}.pdf` });
  return (
    <div className="bg-white border border-nardo-200 rounded-md" data-testid="direccion-boletas">
      <div className="px-6 py-4 border-b border-nardo-200 flex items-center gap-2">
        <BookOpen className="w-4 h-4 text-navy" />
        <h2 className="font-display font-semibold text-xl text-navy">Boletas por trimestre</h2>
      </div>
      <div className="px-6 py-5 space-y-4">
        <p className="text-sm text-nardo-600">Descarga la boleta de un alumno con todas las materias, promedios y calificaciones capturadas por los docentes.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Grupo / grado</label>
            <select value={bolGroup} onChange={(e) => setBolGroup(e.target.value)} data-testid="boleta-group" className="mt-1 w-full border border-nardo-200 rounded-md p-2 text-sm bg-white outline-none focus:border-navy">
              <option value="">Selecciona un grupo</option>
              {grades.map((g) => <option key={g.value} value={g.value}>{g.label || g.value} ({g.count})</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Alumno</label>
            <select value={bolStudent} onChange={(e) => setBolStudent(e.target.value)} disabled={!bolGroup} data-testid="boleta-student" className="mt-1 w-full border border-nardo-200 rounded-md p-2 text-sm bg-white outline-none focus:border-navy disabled:opacity-50">
              <option value="">{bolGroup ? "Selecciona un alumno" : "Elige grupo primero"}</option>
              {bolStudents.map((s) => <option key={s.id} value={s.id}>{s.full_name}</option>)}
            </select>
          </div>
        </div>
        <button onClick={downloadBoleta} disabled={!bolStudent} data-testid="boleta-download" className="inline-flex items-center gap-2 bg-navy hover:bg-navy-light text-white px-4 py-2.5 rounded-md text-sm font-medium transition-colors disabled:opacity-50">
          <Download className="w-4 h-4" /> Descargar boleta PDF
        </button>
      </div>
    </div>
  );
}

/* ==================== BIBLIOTECA Y JUEGOS (Administrador) ==================== */
const RES_LEVELS = ["Todos", "Preescolar", "Primaria", "Secundaria"];
function BibliotecaAdmin() {
  const [custom, setCustom] = useState([]);
  const [defaults, setDefaults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("book");
  const [form, setForm] = useState({ type: "book", title: "", url: "", desc: "", level: "Todos" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/direccion/resources").then(({ data }) => { setCustom(data.custom || []); setDefaults(data.defaults || []); })
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.title.trim() || !form.url.trim()) { toast.error("Escribe título y enlace"); return; }
    setBusy(true);
    try { await api.post("/direccion/resources", { ...form, type }); toast.success("Recurso agregado"); setForm({ type, title: "", url: "", desc: "", level: form.level }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "No se pudo agregar"); } finally { setBusy(false); }
  };
  const remove = async (id) => { try { await api.delete(`/direccion/resources/${id}`); toast.success("Eliminado"); load(); } catch { toast.error("No se pudo eliminar"); } };

  const customOfType = custom.filter((r) => r.type === type);
  const defaultsOfType = defaults.filter((r) => r.type === type);
  const Icon = type === "book" ? BookOpen : Gamepad2;

  return (
    <div className="space-y-5" data-testid="biblioteca-admin">
      <p className="text-sm text-nardo-500 max-w-2xl">Administra los <b>libros</b> y <b>juegos</b> que verán tus alumnos en su portal, segmentados por nivel. El sistema ya incluye una colección gratuita curada; aquí puedes agregar tus propios enlaces.</p>

      <div className="inline-flex rounded-lg bg-nardo-100 p-1">
        <button onClick={() => { setType("book"); setForm((f) => ({ ...f, type: "book" })); }} data-testid="biblio-type-book" className={`px-4 py-1.5 rounded-md text-sm font-medium inline-flex items-center gap-1.5 ${type === "book" ? "bg-white shadow text-navy" : "text-nardo-500"}`}><BookOpen className="w-4 h-4" /> Libros</button>
        <button onClick={() => { setType("game"); setForm((f) => ({ ...f, type: "game" })); }} data-testid="biblio-type-game" className={`px-4 py-1.5 rounded-md text-sm font-medium inline-flex items-center gap-1.5 ${type === "game" ? "bg-white shadow text-navy" : "text-nardo-500"}`}><Gamepad2 className="w-4 h-4" /> Juegos</button>
      </div>

      <div className="bg-white rounded-2xl border border-nardo-200 shadow-sm p-4 grid sm:grid-cols-2 gap-3">
        <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder={type === "book" ? "Título del libro" : "Nombre del juego"} data-testid="biblio-title" className="border border-nardo-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-navy" />
        <input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="Enlace (https://…)" data-testid="biblio-url" className="border border-nardo-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-navy" />
        <input value={form.desc} onChange={(e) => setForm({ ...form, desc: e.target.value })} placeholder="Descripción corta (opcional)" data-testid="biblio-desc" className="border border-nardo-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-navy" />
        <div className="flex gap-2">
          <select value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} data-testid="biblio-level" className="flex-1 border border-nardo-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-navy bg-white">
            {RES_LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <button onClick={add} disabled={busy} data-testid="biblio-add" className="bg-navy hover:bg-navy-light text-white px-4 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Agregar</button>
        </div>
      </div>

      {loading ? <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-navy" /></div> : (
        <>
          <div>
            <h3 className="font-display font-bold text-navy mb-2 flex items-center gap-2"><Icon className="w-4 h-4" /> Tus {type === "book" ? "libros" : "juegos"} ({customOfType.length})</h3>
            {customOfType.length === 0 ? <div className="text-sm text-nardo-400 py-3">Aún no agregas recursos propios.</div> : (
              <div className="grid sm:grid-cols-2 gap-2">
                {customOfType.map((r) => (
                  <div key={r.id} className="flex items-center gap-2 bg-white rounded-xl border border-nardo-200 p-3" data-testid={`biblio-item-${r.id}`}>
                    <div className="flex-1 min-w-0">
                      <a href={r.url} target="_blank" rel="noreferrer" className="font-semibold text-navy text-sm truncate hover:underline flex items-center gap-1">{r.title} <ExternalLink className="w-3 h-3 text-nardo-400" /></a>
                      <div className="text-[11px] text-nardo-400 truncate">{r.level} · {r.desc || r.url}</div>
                    </div>
                    <button onClick={() => remove(r.id)} data-testid={`biblio-del-${r.id}`} className="text-nardo-400 hover:text-red-600 p-1.5"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div>
            <h3 className="font-display font-bold text-navy mb-2 flex items-center gap-2 text-sm"><Library className="w-4 h-4 text-nardo-400" /> Colección incluida ({defaultsOfType.length})</h3>
            <div className="flex flex-wrap gap-1.5">
              {defaultsOfType.map((r, i) => <a key={i} href={r.url} target="_blank" rel="noreferrer" className="text-[11px] bg-nardo-100 hover:bg-nardo-200 text-nardo-600 rounded-full px-2.5 py-1">{r.title} · {r.level}</a>)}
            </div>
          </div>
        </>
      )}
    </div>
  );
}


/* ==================== CLUBS / TALLERES (Dirección) ==================== */
const CLUB_STATUS_LABEL = { presente: "Presente", retardo: "Retardo", justificado: "Justificado", ausente: "Ausente" };
const CLUB_STATUS_CLR = { presente: "text-emerald-600", retardo: "text-amber-600", justificado: "text-slate-500", ausente: "text-red-600" };
function ClubsAdmin() {
  const [data, setData] = useState(null); // {clubs, presets, docentes}
  const [allStudents, setAllStudents] = useState([]);
  const [editing, setEditing] = useState(null); // null | {} | club
  const [attClub, setAttClub] = useState(null);
  const load = () => api.get("/direccion/clubs").then((r) => setData(r.data)).catch(() => setData({ clubs: [], presets: [], docentes: [] }));
  useEffect(() => { load(); api.get("/students", { params: { kind: "student" } }).then((r) => setAllStudents(r.data || [])).catch(() => {}); }, []);
  const remove = async (c) => { if (!window.confirm(`¿Eliminar el club "${c.name}"?`)) return; try { await api.delete(`/direccion/clubs/${c.id}`); toast.success("Club eliminado"); load(); } catch { toast.error("No se pudo eliminar"); } };

  if (!data) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-navy" /></div>;
  if (attClub) return <ClubAttendanceView club={attClub} onBack={() => setAttClub(null)} />;
  if (editing) return <ClubEditor club={editing.id ? editing : null} data={data} allStudents={allStudents} onDone={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />;

  return (
    <div data-testid="clubs-admin">
      <div className="flex items-center justify-between mb-4">
        <div><h2 className="font-display font-bold text-lg text-navy flex items-center gap-2"><Award className="w-5 h-5 text-navy-light" /> Clubs y talleres</h2>
          <p className="text-xs text-nardo-500">Crea clubs, asigna un docente por su correo y agrega a los alumnos. El docente recibe la lista de asistencia en su portal.</p></div>
        <button onClick={() => setEditing({})} data-testid="club-new" className="bg-navy hover:bg-navy-light text-white px-4 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5"><Plus className="w-4 h-4" /> Nuevo club</button>
      </div>
      {data.clubs.length === 0 ? <div className="py-16 text-center text-nardo-500" data-testid="clubs-empty">Aún no hay clubs. Crea el primero con “Nuevo club”.</div> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {data.clubs.map((c) => (
            <div key={c.id} className="bg-white rounded-2xl border border-nardo-200 shadow-sm p-4" data-testid={`club-card-${c.id}`}>
              <div className="flex items-start gap-2">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-navy-light to-navy text-white flex items-center justify-center shrink-0"><Award className="w-5 h-5" /></div>
                <div className="flex-1 min-w-0"><div className="font-bold text-navy">{c.name}</div>
                  <div className="text-[11px] text-nardo-500 truncate">{c.docente_name ? `Prof. ${c.docente_name}` : "Sin docente asignado"}</div>
                  <div className="text-[11px] text-nardo-400">{c.student_count} alumno(s)</div></div>
              </div>
              <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-nardo-100">
                <button onClick={() => setAttClub(c)} data-testid={`club-att-${c.id}`} className="text-[11px] inline-flex items-center gap-1 border border-navy text-navy hover:bg-navy hover:text-white px-2.5 py-1.5 rounded-lg font-semibold transition-colors"><ClipboardCheck className="w-3.5 h-3.5" /> Asistencia</button>
                <button onClick={() => setEditing(c)} data-testid={`club-edit-${c.id}`} className="text-[11px] inline-flex items-center gap-1 text-nardo-600 hover:text-navy px-2 py-1.5 rounded-lg hover:bg-nardo-100"><Pencil className="w-3.5 h-3.5" /> Editar</button>
                <button onClick={() => remove(c)} data-testid={`club-del-${c.id}`} className="ml-auto text-nardo-400 hover:text-red-600 p-1.5"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ClubEditor({ club, data, allStudents, onDone, onCancel }) {
  const [name, setName] = useState(club?.name || "");
  const [custom, setCustom] = useState(!club ? false : !(data.presets || []).includes(club?.name));
  const [docenteId, setDocenteId] = useState(club?.docente_user_id || "");
  const [ids, setIds] = useState(club?.student_ids || []);
  const [q, setQ] = useState("");
  const [grp, setGrp] = useState("");
  const [saving, setSaving] = useState(false);
  const groups = useMemo(() => Array.from(new Set(allStudents.map((s) => s.group).filter(Boolean))).sort(), [allStudents]);
  const filtered = allStudents.filter((s) => (!grp || s.group === grp) && (!q || (s.full_name || "").toLowerCase().includes(q.toLowerCase())));
  const toggle = (id) => setIds((a) => (a.includes(id) ? a.filter((x) => x !== id) : [...a, id]));
  const save = async () => {
    if (!name.trim()) { toast.error("Escribe el nombre del club"); return; }
    setSaving(true);
    try {
      const body = { name: name.trim(), docente_user_id: docenteId || null, student_ids: ids };
      if (club?.id) await api.put(`/direccion/clubs/${club.id}`, body);
      else await api.post("/direccion/clubs", body);
      toast.success(club?.id ? "Club actualizado" : "Club creado");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); } finally { setSaving(false); }
  };
  return (
    <div data-testid="club-editor">
      <div className="flex items-center gap-3 mb-4"><button onClick={onCancel} className="text-nardo-500 hover:text-navy"><ChevronLeft className="w-5 h-5" /></button>
        <h2 className="font-display font-bold text-lg text-navy">{club?.id ? "Editar club" : "Nuevo club"}</h2></div>
      <div className="bg-white rounded-2xl border border-nardo-200 shadow-sm p-4 space-y-4">
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-navy-light block mb-1.5">Tipo de club</label>
          {!custom ? (
            <div className="flex flex-wrap gap-1.5">
              {(data.presets || []).map((p) => <button key={p} onClick={() => setName(p)} data-testid={`club-preset-${p}`} className={`text-xs px-3 py-1.5 rounded-full border ${name === p ? "bg-navy text-white border-navy" : "bg-white text-nardo-600 border-nardo-200 hover:border-navy"}`}>{p}</button>)}
              <button onClick={() => { setCustom(true); setName(""); }} data-testid="club-custom-btn" className="text-xs px-3 py-1.5 rounded-full border border-dashed border-nardo-300 text-nardo-500 inline-flex items-center gap-1"><Pencil className="w-3 h-3" /> Escribir</button>
            </div>
          ) : (
            <div className="flex gap-2">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre del club" data-testid="club-name" className="flex-1 border border-nardo-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-navy" />
              {(data.presets || []).length > 0 && <button onClick={() => setCustom(false)} className="text-xs text-nardo-500 px-2">Ver lista</button>}
            </div>
          )}
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.2em] text-navy-light block mb-1">Docente (correo institucional)</label>
          <select value={docenteId} onChange={(e) => setDocenteId(e.target.value)} data-testid="club-docente" className="w-full border border-nardo-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-navy bg-white">
            <option value="">— Sin docente —</option>
            {(data.docentes || []).map((d) => <option key={d.id} value={d.id}>{d.name} · {d.email}</option>)}
          </select>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-[10px] uppercase tracking-[0.2em] text-navy-light">Alumnos del club ({ids.length})</label>
          </div>
          <div className="flex flex-wrap gap-2 mb-2">
            <div className="relative flex-1 min-w-[160px]"><Search className="w-3.5 h-3.5 text-nardo-400 absolute left-2.5 top-2.5" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar alumno…" data-testid="club-search" className="w-full border border-nardo-200 rounded-lg pl-8 pr-3 py-1.5 text-sm outline-none focus:border-navy" /></div>
            <select value={grp} onChange={(e) => setGrp(e.target.value)} data-testid="club-group-filter" className="border border-nardo-200 rounded-lg px-3 py-1.5 text-sm outline-none focus:border-navy bg-white">
              <option value="">Todos los grupos</option>
              {groups.map((g) => <option key={g} value={g}>{groupLabel(g)}</option>)}
            </select>
          </div>
          <div className="max-h-64 overflow-y-auto grid grid-cols-1 sm:grid-cols-2 gap-1 border border-nardo-100 rounded-lg p-2" data-testid="club-student-picker">
            {filtered.length === 0 && <div className="text-[11px] text-nardo-400 py-2 col-span-2 text-center">Sin alumnos.</div>}
            {filtered.map((s) => (
              <label key={s.id} data-testid={`club-pick-${s.id}`} className={`flex items-center gap-2 text-xs rounded-lg px-2.5 py-1.5 cursor-pointer border ${ids.includes(s.id) ? "bg-navy/5 border-navy" : "bg-white border-nardo-200 hover:border-navy"}`}>
                <input type="checkbox" checked={ids.includes(s.id)} onChange={() => toggle(s.id)} className="accent-navy" />
                <span className="truncate text-navy flex-1">{s.full_name}</span>
                <span className="text-[10px] text-nardo-400">{groupLabel(s.group)}</span>
              </label>
            ))}
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onCancel} className="text-sm px-4 py-2 rounded-lg text-nardo-600 hover:bg-nardo-100">Cancelar</button>
          <button onClick={save} disabled={saving} data-testid="club-save-admin" className="text-sm px-4 py-2 rounded-lg bg-navy hover:bg-navy-light text-white font-semibold inline-flex items-center gap-1.5 disabled:opacity-50">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Guardar club</button>
        </div>
      </div>
    </div>
  );
}

const CLUB_RANGES_ADMIN = [{ id: "day", label: "Día" }, { id: "week", label: "Semana" }, { id: "fortnight", label: "Quincena" }, { id: "month", label: "Mes" }];
function ClubAttendanceView({ club, onBack }) {
  const [info, setInfo] = useState(null);
  const [date, setDate] = useState("");
  const [dlRange, setDlRange] = useState("day");
  const [exporting, setExporting] = useState(null);
  const [imgData, setImgData] = useState(null);
  const reportRef = useRef(null);
  useEffect(() => { api.get(`/direccion/clubs/${club.id}/attendance`).then((r) => { setInfo(r.data); setDate((r.data.history[0]?.date) || ""); }).catch(() => setInfo({ students: [], history: [] })); }, [club.id]);
  const dlDate = date || new Date().toISOString().slice(0, 10);
  const stLabelI = { presente: "Presente", retardo: "Retardo", justificado: "Justificado", ausente: "Ausente" };
  const downloadClubPdf = () => downloadPdfFile(`/direccion/clubs/${club.id}/attendance/pdf`, { params: { range: dlRange, date: dlDate }, filename: `club_${club.name}_${dlRange}_${dlDate}.pdf` });
  const downloadClubImage = async () => {
    setExporting(true);
    try {
      const { data } = await api.get(`/direccion/clubs/${club.id}/attendance/summary`, { params: { range: dlRange, date: dlDate } });
      setImgData(data);
      await new Promise((r) => setTimeout(r, 200));
      const png = await toPng(reportRef.current, { pixelRatio: 2, cacheBust: true, backgroundColor: "#ffffff" });
      const a = document.createElement("a"); a.href = png; a.download = `club_${club.name}_${dlRange}_${dlDate}.png`; a.click();
      toast.success("Imagen descargada");
    } catch { toast.error("No se pudo generar la imagen"); } finally { setExporting(false); }
  };
  if (!info) return <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-navy" /></div>;
  const day = info.history.find((h) => h.date === date);
  return (
    <div data-testid="club-att-view">
      <div className="flex items-center gap-3 mb-4"><button onClick={onBack} className="text-nardo-500 hover:text-navy"><ChevronLeft className="w-5 h-5" /></button>
        <div className="flex items-center gap-2"><Award className="w-5 h-5 text-navy-light" /><h2 className="font-display font-bold text-lg text-navy">{club.name} · Asistencia</h2></div>
        {info.history.length > 0 && (
          <select value={date} onChange={(e) => setDate(e.target.value)} data-testid="club-att-date" className="ml-auto border border-nardo-200 rounded-lg px-3 py-1.5 text-sm outline-none focus:border-navy bg-white">
            {info.history.map((h) => <option key={h.date} value={h.date}>{h.date}</option>)}
          </select>
        )}
      </div>

      <div className="mb-4 bg-white rounded-2xl border border-nardo-200 shadow-sm p-4" data-testid="club-admin-downloads">
        <div className="text-[10px] uppercase tracking-[0.2em] text-navy-light mb-2 flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> Descargar expediente de asistencia</div>
        <div className="flex flex-wrap items-center gap-2">
          <input type="date" value={dlDate} onChange={(e) => setDate(e.target.value)} data-testid="club-admin-date" className="border border-nardo-200 rounded-lg px-3 py-1.5 text-sm outline-none focus:border-navy" />
          <div className="inline-flex rounded-lg bg-nardo-100 p-1">
            {CLUB_RANGES_ADMIN.map((r) => <button key={r.id} onClick={() => setDlRange(r.id)} data-testid={`club-admin-range-${r.id}`} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${dlRange === r.id ? "bg-navy text-white" : "text-nardo-600"}`}>{r.label}</button>)}
          </div>
          <div className="flex gap-2 ml-auto">
            <button onClick={downloadClubPdf} data-testid="club-admin-dl-pdf" className="text-xs inline-flex items-center gap-1.5 border border-navy text-navy hover:bg-navy hover:text-white px-3 py-2 rounded-lg font-semibold transition-colors"><FileText className="w-4 h-4" /> PDF</button>
            <button onClick={downloadClubImage} disabled={!!exporting} data-testid="club-admin-dl-image" className="text-xs inline-flex items-center gap-1.5 border border-navy text-navy hover:bg-navy hover:text-white px-3 py-2 rounded-lg font-semibold disabled:opacity-50 transition-colors">{exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />} Imagen</button>
          </div>
        </div>
      </div>

      {info.history.length === 0 ? <div className="py-12 text-center text-nardo-500">Aún no hay registros de asistencia. El docente los captura desde su portal.</div> : (
        <div className="bg-white rounded-2xl border border-nardo-200 shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-nardo-50 text-nardo-500 text-[11px] uppercase tracking-wide">
              <tr><th className="text-left px-4 py-2">Alumno</th><th className="text-left px-4 py-2">Grupo</th><th className="text-left px-4 py-2">Estado</th></tr>
            </thead>
            <tbody>
              {info.students.map((s) => { const st = day?.records?.[s.id]; return (
                <tr key={s.id} className="border-t border-nardo-100" data-testid={`club-att-row-${s.id}`}>
                  <td className="px-4 py-2 text-navy font-medium">{s.full_name}</td>
                  <td className="px-4 py-2 text-nardo-500">{groupLabel(s.group)}</td>
                  <td className={`px-4 py-2 font-semibold ${CLUB_STATUS_CLR[st] || "text-nardo-400"}`}>{CLUB_STATUS_LABEL[st] || "Sin registro"}</td>
                </tr>
              ); })}
            </tbody>
          </table>
          {day?.updated_by && <div className="text-[11px] text-nardo-400 px-4 py-2 border-t border-nardo-100">Registrado por: {day.updated_by}</div>}
        </div>
      )}

      {imgData && (
        <div style={{ position: "fixed", left: -99999, top: 0 }} aria-hidden>
          <div ref={reportRef} style={{ width: 760, background: "#ffffff", padding: 32, fontFamily: "Manrope, Arial, sans-serif", color: "#112240" }} data-testid="club-admin-report-node">
            <div style={{ display: "flex", alignItems: "center", gap: 14, borderBottom: "3px solid #112240", paddingBottom: 14, marginBottom: 16 }}>
              <div style={{ width: 46, height: 46, background: "#112240", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 22, borderRadius: 8 }}>{(imgData.school_name || "U").slice(0, 1)}</div>
              <div><div style={{ fontSize: 12, letterSpacing: 3, textTransform: "uppercase", color: "#1e3a8a" }}>ZAVIA</div>
                <div style={{ fontSize: 22, fontWeight: 800 }}>{imgData.school_name || "Colegio"}</div></div>
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 8 }}>Asistencia de Club · {imgData.club?.name}</div>
            <div style={{ fontSize: 13, color: "#334155", marginBottom: 4 }}>Docente: <b>{imgData.docente_name || "—"}</b></div>
            <div style={{ fontSize: 13, color: "#334155", marginBottom: 14 }}>Periodo: <b>{imgData.label}</b> &nbsp;·&nbsp; Sesiones: <b>{imgData.dates?.length || 0}</b></div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#112240", color: "#fff" }}>
                  <th style={{ textAlign: "center", padding: "8px 6px", width: 36 }}>#</th>
                  <th style={{ textAlign: "left", padding: "8px 10px" }}>Alumno</th>
                  <th style={{ textAlign: "left", padding: "8px 10px", width: 120 }}>Grupo</th>
                  {imgData.rng === "day"
                    ? <th style={{ textAlign: "left", padding: "8px 10px", width: 120 }}>Estado</th>
                    : <><th style={{ textAlign: "center", padding: "8px 6px" }}>Pres.</th><th style={{ textAlign: "center", padding: "8px 6px" }}>Ret.</th><th style={{ textAlign: "center", padding: "8px 6px" }}>Just.</th><th style={{ textAlign: "center", padding: "8px 6px" }}>Aus.</th><th style={{ textAlign: "center", padding: "8px 6px" }}>Ses.</th></>}
                </tr>
              </thead>
              <tbody>
                {(imgData.rows || []).map((r, i) => (
                  <tr key={r.id} style={{ background: i % 2 ? "#f6f7f8" : "#fff" }}>
                    <td style={{ textAlign: "center", padding: "7px 6px", color: "#94a3b8" }}>{i + 1}</td>
                    <td style={{ padding: "7px 10px", fontWeight: 600 }}>{r.full_name}</td>
                    <td style={{ padding: "7px 10px", color: "#475569" }}>{r.group_label}</td>
                    {imgData.rng === "day"
                      ? <td style={{ padding: "7px 10px", fontWeight: 700, color: { presente: "#059669", ausente: "#dc2626", retardo: "#d97706", justificado: "#0284c7" }[r.day_status] || "#94a3b8" }}>{stLabelI[r.day_status] || "Sin registro"}</td>
                      : <><td style={{ textAlign: "center", padding: "7px 6px", color: "#059669", fontWeight: 700 }}>{r.counts.presente}</td><td style={{ textAlign: "center", padding: "7px 6px", color: "#d97706", fontWeight: 700 }}>{r.counts.retardo}</td><td style={{ textAlign: "center", padding: "7px 6px", color: "#0284c7", fontWeight: 700 }}>{r.counts.justificado}</td><td style={{ textAlign: "center", padding: "7px 6px", color: "#dc2626", fontWeight: 700 }}>{r.counts.ausente}</td><td style={{ textAlign: "center", padding: "7px 6px", fontWeight: 700 }}>{r.sessions}</td></>}
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ fontSize: 10, color: "#64748b", marginTop: 14 }}>Asistencia de club guardada como expediente del colegio.</div>
          </div>
        </div>
      )}
    </div>
  );
}


function GastosInternos() {
  const nowMonth = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };
  const [month, setMonth] = useState(nowMonth());
  const [data, setData] = useState({ expenses: [], total: 0, subtotals: [], categories: [], month_label: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // formulario nuevo gasto
  const [date, setDate] = useState("");
  const [concept, setConcept] = useState("");
  const [category, setCategory] = useState("");
  const [method, setMethod] = useState("Efectivo");
  const [amount, setAmount] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/direccion/expenses", { params: { month } });
      setData(d);
      if (!category && d.categories?.length) setCategory(d.categories[0]);
    } catch { toast.error("No se pudieron cargar los gastos"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const add = async () => {
    if (!concept.trim()) { toast.error("Escribe el concepto"); return; }
    if (!amount || Number(amount) <= 0) { toast.error("Escribe un importe válido"); return; }
    setSaving(true);
    try {
      await api.post("/direccion/expenses", { month, date: date || `${month}-01`, concept: concept.trim(), category, method, amount: Number(amount) });
      setConcept(""); setAmount(""); setDate("");
      toast.success("Gasto registrado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo registrar"); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este gasto?")) return;
    try { await api.delete(`/direccion/expenses/${id}`); toast.success("Gasto eliminado"); load(); }
    catch { toast.error("No se pudo eliminar"); }
  };

  const money = (n) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="space-y-4" data-testid="direccion-gastos">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Banknote className="w-5 h-5 text-navy" />
          <h3 className="font-display font-bold text-lg text-navy">Gastos internos</h3>
        </div>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="gastos-month" className="border border-nardo-200 rounded-lg px-3 py-1.5 text-sm text-navy outline-none focus:border-navy" />
        <div className="flex-1" />
        <button onClick={() => downloadPdfFile("/direccion/expenses/pdf", { params: { month }, filename: `gastos_${month}.pdf` })} data-testid="gastos-pdf" className="inline-flex items-center gap-1.5 text-xs border border-navy/30 text-navy hover:bg-navy hover:text-white rounded-lg px-3 py-2 font-semibold transition-colors"><FileText className="w-4 h-4" /> PDF</button>
        <button onClick={() => downloadPdfFile("/direccion/expenses/image", { params: { month }, filename: `gastos_${month}.png` })} data-testid="gastos-img" className="inline-flex items-center gap-1.5 text-xs border border-navy/30 text-navy hover:bg-navy hover:text-white rounded-lg px-3 py-2 font-semibold transition-colors"><ImageIcon className="w-4 h-4" /> Imagen</button>
      </div>

      {/* Alta rápida */}
      <div className="bg-white rounded-2xl border border-nardo-200 shadow-sm p-4">
        <div className="grid grid-cols-2 md:grid-cols-[120px_1fr_160px_150px_120px_auto] gap-2 items-end">
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-nardo-500 block mb-1">Fecha</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="gastos-date" className="w-full border border-nardo-200 rounded-lg px-2.5 py-2 text-sm text-navy outline-none focus:border-navy" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-nardo-500 block mb-1">Concepto</label>
            <input value={concept} onChange={(e) => setConcept(e.target.value)} data-testid="gastos-concept" placeholder="Ej. Gasolina camioneta" className="w-full border border-nardo-200 rounded-lg px-2.5 py-2 text-sm text-navy outline-none focus:border-navy" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-nardo-500 block mb-1">Categoría</label>
            <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="gastos-category" className="w-full border border-nardo-200 rounded-lg px-2.5 py-2 text-sm text-navy outline-none focus:border-navy">
              {(data.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-nardo-500 block mb-1">Método</label>
            <select value={method} onChange={(e) => setMethod(e.target.value)} data-testid="gastos-method" className="w-full border border-nardo-200 rounded-lg px-2.5 py-2 text-sm text-navy outline-none focus:border-navy">
              <option value="Efectivo">Efectivo</option>
              <option value="Transferencia">Transferencia</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-nardo-500 block mb-1">Importe</label>
            <input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} data-testid="gastos-amount" placeholder="0.00" className="w-full border border-nardo-200 rounded-lg px-2.5 py-2 text-sm text-navy outline-none focus:border-navy" />
          </div>
          <button onClick={add} disabled={saving} data-testid="gastos-add" className="inline-flex items-center justify-center gap-1.5 bg-navy hover:bg-navy-light text-white rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50 h-[38px]">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Agregar
          </button>
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-2xl border border-nardo-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="py-12 text-center text-nardo-500 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Cargando…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-navy text-white text-[11px] uppercase tracking-wider">
                  <th className="text-left px-4 py-2.5 w-28">Fecha</th>
                  <th className="text-left px-4 py-2.5">Concepto</th>
                  <th className="text-left px-4 py-2.5 w-52">Categoría</th>
                  <th className="text-right px-4 py-2.5 w-32">Importe</th>
                  <th className="px-2 py-2.5 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-nardo-100">
                {data.expenses.length === 0 ? (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-nardo-400 text-sm">No hay gastos registrados en {data.month_label}. Agrega el primero arriba.</td></tr>
                ) : data.expenses.map((e) => (
                  <tr key={e.id} data-testid={`gasto-row-${e.id}`} className="hover:bg-nardo-50/60">
                    <td className="px-4 py-2.5 text-nardo-600">{e.date}</td>
                    <td className="px-4 py-2.5 text-navy font-medium">{e.concept}</td>
                    <td className="px-4 py-2.5"><span className="text-[11px] px-2 py-0.5 rounded-full bg-navy/10 text-navy font-medium">{e.category}</span></td>
                    <td className="px-4 py-2.5 text-right font-semibold text-navy tabular-nums">{money(e.amount)}</td>
                    <td className="px-2 py-2.5 text-center"><button onClick={() => del(e.id)} data-testid={`gasto-del-${e.id}`} className="text-nardo-400 hover:text-red-600 p-1"><Trash2 className="w-4 h-4" /></button></td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-navy/5 border-t-2 border-navy/20">
                  <td colSpan={3} className="px-4 py-3 text-right font-bold text-navy uppercase text-xs tracking-wider">Total del mes</td>
                  <td className="px-4 py-3 text-right font-bold text-lg text-navy tabular-nums" data-testid="gastos-total">{money(data.total)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Subtotales por categoría */}
      {data.subtotals?.length > 0 && (
        <div className="bg-white rounded-2xl border border-nardo-200 shadow-sm p-4">
          <div className="text-[11px] uppercase tracking-[0.15em] text-nardo-500 mb-3 font-semibold">Subtotal por categoría</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {data.subtotals.map((s) => (
              <div key={s.category} className="flex items-center justify-between text-sm border border-nardo-100 rounded-lg px-3 py-2" data-testid={`gasto-sub-${s.category}`}>
                <span className="text-nardo-600 truncate">{s.category}</span>
                <span className="font-semibold text-navy tabular-nums">{money(s.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


function DosificacionOverview() {
  const [groups, setGroups] = useState(null);
  const [ciclo, setCiclo] = useState("");
  const [open, setOpen] = useState(null);

  useEffect(() => {
    api.get("/direccion/dosificacion-overview")
      .then(({ data }) => { setGroups(data.groups || []); setCiclo(data.ciclo || ""); })
      .catch(() => setGroups([]));
  }, []);

  if (groups === null) return <div className="py-16 text-center text-nardo-500 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Cargando…</div>;

  return (
    <div className="space-y-4" data-testid="direccion-dosificacion">
      <div className="flex items-center gap-2">
        <CalendarClock className="w-5 h-5 text-navy" />
        <h3 className="font-display font-bold text-lg text-navy">Avance de temario por grupo</h3>
        {ciclo && <span className="ml-auto text-xs text-nardo-500">Ciclo {ciclo}</span>}
      </div>
      {groups.length === 0 ? (
        <div className="bg-white rounded-2xl border border-nardo-200 py-14 text-center text-nardo-500 text-sm">
          <CalendarClock className="w-10 h-10 mx-auto mb-3 text-nardo-300" />
          Los docentes aún no capturan su dosificación. Aparecerá aquí en cuanto empiecen.
        </div>
      ) : groups.map((g) => (
        <div key={g.group} data-testid={`dir-dos-group-${g.group}`} className="bg-white rounded-2xl border border-nardo-200 shadow-sm overflow-hidden">
          <button onClick={() => setOpen(open === g.group ? null : g.group)} className="w-full flex items-center gap-3 px-4 py-3 text-left">
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-navy">{g.group_label}</div>
              <div className="text-[11px] text-nardo-400">{g.subjects.length} materia(s) con temario</div>
            </div>
            <div className="w-32 shrink-0">
              <div className="text-[10px] text-right text-nardo-500 mb-0.5">Promedio {g.avg_progress}%</div>
              <div className="h-2 rounded-full bg-nardo-200 overflow-hidden"><div className="h-full bg-emerald-500" style={{ width: `${g.avg_progress}%` }} /></div>
            </div>
            <ChevronDown className={`w-4 h-4 text-nardo-400 transition-transform ${open === g.group ? "rotate-180" : ""}`} />
          </button>
          {open === g.group && (
            <div className="border-t border-nardo-100 divide-y divide-nardo-100">
              {g.subjects.map((s, i) => (
                <div key={i} className="px-4 py-3 flex items-center gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-navy font-medium truncate">{s.subject}</div>
                    <div className="text-[11px] text-nardo-400">{s.docente_name || "Docente"} · {s.done}/{s.total} temas · {s.in_progress} en curso</div>
                  </div>
                  <div className="w-28 shrink-0">
                    <div className="text-[10px] text-right text-nardo-500 mb-0.5">{s.progress}%</div>
                    <div className="h-1.5 rounded-full bg-nardo-200 overflow-hidden"><div className={`h-full ${s.progress >= 66 ? "bg-emerald-500" : s.progress >= 33 ? "bg-amber-500" : "bg-red-400"}`} style={{ width: `${s.progress}%` }} /></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}


const ADMIN_EXTRA_TABS = [
  { id: "asistencias", label: "Asistencias en línea", icon: ClipboardCheck },
  { id: "calificaciones", label: "Calificaciones", icon: BookOpen },
];
// Tabs disponibles para correos administrativos (staff) dentro de "Área administrativa".
const STAFF_TAB_IDS = ["tablero", "comunicados", "documentos", "calendario", "justificantes", "credenciales", "recibos", "gastos", "incidencias", "horarios", "comedor", "aula", "asistencias", "calificaciones", "bajas"];

// Portal de Coordinación (revisión de todos los grupos + chat, música y banco de recursos).
const COORD_TABS = [
  { id: "asistencias", label: "Asistencia de grupos", icon: ClipboardCheck },
  { id: "planeaciones", label: "Revisión de planeaciones", icon: FileCheck2 },
  { id: "califdocentes", label: "Calificaciones por docente", icon: GraduationCap },
  { id: "aula-super", label: "Aula Virtual", icon: Presentation },
  { id: "calificaciones", label: "Calificaciones y boletas", icon: BookOpen },
  { id: "horarios", label: "Horarios", icon: CalendarClock },
  { id: "incidencias", label: "Bitácora Digital", icon: AlertTriangle },
  { id: "club-comunicados", label: "Comunicados de clubs", icon: Megaphone },
  { id: "musica", label: "Música", icon: Music },
  { id: "recursos", label: "Banco de recursos", icon: Library },
];

const AULA_TYPE_BADGE = {
  tarea: { label: "Tarea", cls: "bg-navy text-white" },
  anuncio: { label: "Aviso", cls: "bg-amber-500 text-white" },
  material: { label: "Material", cls: "bg-emerald-600 text-white" },
};

function AulaStat({ icon: Icon, label, value, tone = "navy" }) {
  const tones = {
    navy: "text-navy", emerald: "text-emerald-600", amber: "text-amber-600", red: "text-red-600", sky: "text-sky-600",
  };
  return (
    <div className="bg-white border border-nardo-200 rounded-xl p-4" data-testid={`aula-stat-${label}`}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.15em] text-nardo-400"><Icon className="w-3.5 h-3.5" /> {label}</div>
      <div className={`text-2xl font-display font-extrabold mt-1 ${tones[tone] || tones.navy}`}>{value}</div>
    </div>
  );
}

function AulaAdmin() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/aula/admin/overview")
      .then(({ data }) => { if (alive) setData(data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  if (loading) return <div className="py-16 text-center text-nardo-500 text-sm" data-testid="aula-admin-loading"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Cargando estadísticas…</div>;
  if (!data) return <div className="py-16 text-center text-nardo-500 text-sm">No se pudieron cargar las estadísticas del Aula.</div>;

  const t = data.totals;
  const rateColor = (r) => (r >= 80 ? "bg-emerald-500" : r >= 50 ? "bg-amber-500" : "bg-red-500");

  return (
    <div className="space-y-6" data-testid="aula-admin">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <AulaStat icon={Presentation} label="Publicaciones" value={t.posts} />
        <AulaStat icon={ClipboardCheck} label="Tareas activas" value={t.activas} tone="sky" />
        <AulaStat icon={CalendarClock} label="Tareas vencidas" value={t.vencidas} tone="red" />
        <AulaStat icon={Check} label="Entregas" value={t.entregas} tone="emerald" />
        <AulaStat icon={Award} label="Calificadas" value={t.calificadas} tone="navy" />
        <AulaStat icon={TrendingUp} label="Promedio gral." value={t.avg_grade ?? "—"} tone="amber" />
      </div>

      <div className="bg-white border border-nardo-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-nardo-100 font-display font-bold text-navy flex items-center gap-2"><Users className="w-4 h-4 text-navy-light" /> Desempeño por grupo</div>
        {data.groups.length === 0 ? (
          <div className="py-10 text-center text-nardo-500 text-sm" data-testid="aula-admin-empty">Aún no hay publicaciones del Aula Virtual.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-navy/5 text-navy text-[11px] uppercase tracking-wider">
                  <th className="text-left px-4 py-2.5">Grupo</th>
                  <th className="px-2 py-2.5 w-20">Alumnos</th>
                  <th className="px-2 py-2.5 w-16">Tareas</th>
                  <th className="px-4 py-2.5 w-56">% Entregas</th>
                  <th className="px-2 py-2.5 w-20">Promedio</th>
                  <th className="px-2 py-2.5 w-24">Boleta</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-nardo-100">
                {data.groups.map((g) => (
                  <tr key={g.group} data-testid={`aula-admin-group-${g.group}`}>
                    <td className="px-4 py-2.5 font-medium text-navy">{g.label}</td>
                    <td className="px-2 py-2.5 text-center text-nardo-600">{g.students}</td>
                    <td className="px-2 py-2.5 text-center text-nardo-600">{g.tareas}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 rounded-full bg-nardo-100 overflow-hidden">
                          <div className={`h-full rounded-full ${rateColor(g.delivered_rate)}`} style={{ width: `${g.delivered_rate}%` }} />
                        </div>
                        <span className="text-[11px] text-nardo-500 w-24 text-right">{g.delivered_rate}% · {g.delivered}/{g.expected}</span>
                      </div>
                    </td>
                    <td className={`px-2 py-2.5 text-center font-bold ${g.avg_grade == null ? "text-nardo-300" : (g.avg_grade < 7 ? "text-red-600" : "text-emerald-600")}`}>{g.avg_grade ?? "—"}</td>
                    <td className="px-2 py-2.5 text-center">
                      <button onClick={() => downloadPdfFile(`/aula/tasks-pdf/group`, { params: { group: g.group }, filename: `boleta_tareas_${g.group}.pdf` })} data-testid={`aula-admin-boleta-${g.group}`} className="inline-flex items-center gap-1 text-[11px] text-navy border border-navy/30 hover:bg-navy hover:text-white rounded-lg px-2.5 py-1.5 transition-colors"><Download className="w-3.5 h-3.5" /> PDF</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-white border border-nardo-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-nardo-100 font-display font-bold text-navy flex items-center gap-2"><Clock className="w-4 h-4 text-navy-light" /> Publicaciones recientes</div>
        {data.recent.length === 0 ? (
          <div className="py-8 text-center text-nardo-500 text-sm">Sin publicaciones.</div>
        ) : (
          <div className="divide-y divide-nardo-100">
            {data.recent.map((p) => {
              const b = AULA_TYPE_BADGE[p.type] || AULA_TYPE_BADGE.tarea;
              return (
                <div key={p.id} className="px-4 py-3 flex items-center gap-3" data-testid={`aula-admin-recent-${p.id}`}>
                  <span className={`shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full ${b.cls}`}>{b.label}</span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-navy font-medium truncate">{p.title}</div>
                    <div className="text-[11px] text-nardo-400">{p.label} · {p.docente_name} · {(p.created_at || "").slice(0, 10)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- Supervisión del Aula Virtual (Coordinación / Dirección) ---------------- */
const SUP_KIND = {
  aula: { label: "Aula", cls: "bg-navy text-white" },
  recurso: { label: "Recurso", cls: "bg-emerald-600 text-white" },
  club: { label: "Club", cls: "bg-amber-500 text-white" },
  enfermeria: { label: "Enfermería", cls: "bg-rose-500 text-white" },
};

function SupInitials({ name }) {
  const ini = (name || "?").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  return <div className="w-11 h-11 rounded-full bg-navy/10 text-navy font-display font-bold flex items-center justify-center shrink-0">{ini}</div>;
}

function SupItemDetail({ item, onDelete }) {
  const k = SUP_KIND[item.kind] || SUP_KIND.aula;
  const fmt = (s) => (s ? new Date(s).toLocaleString("es-MX", { dateStyle: "medium", timeStyle: "short" }) : "—");
  return (
    <div className="space-y-4" data-testid="sup-detail">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${k.cls}`}>{item.type_label}</span>
        <span className="text-[11px] text-nardo-500">{item.group_label}{item.subject ? ` · ${item.subject}` : ""}</span>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wider text-nardo-400">Publicado</div>
        <div className="text-sm text-navy">{fmt(item.created_at)}{item.due_at ? ` · Entrega: ${fmt(item.due_at)}` : ""}</div>
      </div>
      {item.description ? (
        <div>
          <div className="text-[11px] uppercase tracking-wider text-nardo-400 mb-1">Descripción</div>
          <div className="text-sm text-nardo-700 whitespace-pre-wrap">{item.description}</div>
        </div>
      ) : null}
      {(item.attachments || []).length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wider text-nardo-400 mb-1">Archivos adjuntos</div>
          <div className="flex flex-wrap gap-2">
            {item.attachments.map((a, i) => (
              <a key={i} href={`${API}${a.url}`} target="_blank" rel="noreferrer" data-testid={`sup-attach-${item.id}-${i}`}
                className="inline-flex items-center gap-1.5 border border-navy/30 text-navy hover:bg-navy hover:text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors">
                <Paperclip className="w-3.5 h-3.5" /> {a.name}
              </a>
            ))}
          </div>
        </div>
      )}
      {(item.links || []).length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wider text-nardo-400 mb-1">Enlaces</div>
          <div className="flex flex-col gap-1.5">
            {item.links.map((l, i) => (
              <a key={i} href={l} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-navy hover:underline break-all"><ExternalLink className="w-3.5 h-3.5 shrink-0" /> {l}</a>
            ))}
          </div>
        </div>
      )}
      {(item.youtube || []).length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wider text-nardo-400 mb-1">Videos</div>
          <div className="flex flex-col gap-1.5">
            {item.youtube.map((l, i) => (
              <a key={i} href={l} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-navy hover:underline break-all"><ExternalLink className="w-3.5 h-3.5 shrink-0" /> {l}</a>
            ))}
          </div>
        </div>
      )}
      {item.can_delete && (
        <div className="pt-2 border-t border-nardo-100">
          <button onClick={() => onDelete(item)} data-testid={`sup-detail-delete-${item.id}`}
            className="inline-flex items-center gap-1.5 text-xs font-semibold border border-red-300 text-red-600 hover:bg-red-600 hover:text-white px-3 py-2 rounded-lg transition-colors">
            <Trash2 className="w-4 h-4" /> Eliminar publicación
          </button>
        </div>
      )}
    </div>
  );
}

function AulaSupervision() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});
  const [detail, setDetail] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");

  const load = () => { setLoading(true); api.get("/aula/supervision").then(({ data }) => setData(data)).catch(() => setData(null)).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const del = async (it) => {
    if (!window.confirm(`¿Eliminar "${it.title}"? Esta acción no se puede deshacer.`)) return;
    try { await api.delete(`/aula/supervision/${it.kind}/${it.id}`); toast.success("Publicación eliminada"); setDetail(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo eliminar"); }
  };

  const teachers = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (data?.teachers || []).map((t) => ({
      ...t,
      items: (t.items || []).filter((it) =>
        (filter === "all" || it.kind === filter) &&
        (!needle || (it.title || "").toLowerCase().includes(needle) || (it.description || "").toLowerCase().includes(needle))),
    })).filter((t) => t.items.length > 0);
  }, [data, q, filter]);

  if (loading) return <div className="py-16 text-center text-nardo-500 text-sm" data-testid="sup-loading"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Cargando publicaciones…</div>;
  if (!data) return <div className="py-16 text-center text-nardo-500 text-sm">No se pudo cargar la supervisión del Aula.</div>;

  const t = data.totals || {};
  const chips = [
    { id: "all", label: `Todo (${t.total || 0})` },
    { id: "aula", label: `Aula (${t.aula || 0})` },
    { id: "recurso", label: `Recursos (${t.recurso || 0})` },
    { id: "club", label: `Clubs (${t.club || 0})` },
    { id: "enfermeria", label: `Enfermería (${t.enfermeria || 0})` },
  ];
  const fmtDate = (s) => (s ? new Date(s).toLocaleString("es-MX", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

  return (
    <div className="space-y-5" data-testid="aula-supervision">
      <div>
        <h2 className="font-display font-semibold text-xl text-navy flex items-center gap-2"><Presentation className="w-5 h-5 text-navy-light" /> Supervisión del Aula Virtual</h2>
        <p className="text-sm text-nardo-500 mt-0.5">Todo lo que sube el personal (tareas, avisos, materiales, recursos, comunicados de clubs y enfermería), organizado por docente.</p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-nardo-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por título o descripción…" data-testid="sup-search"
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-nardo-200 text-sm focus:outline-none focus:ring-2 focus:ring-navy/20" />
        </div>
        <button onClick={load} data-testid="sup-refresh" className="inline-flex items-center gap-1.5 text-xs font-semibold border border-nardo-200 text-nardo-600 hover:bg-nardo-50 px-3 py-2 rounded-lg transition-colors"><RefreshCw className="w-4 h-4" /> Actualizar</button>
      </div>
      <div className="flex flex-wrap gap-2">
        {chips.map((c) => (
          <button key={c.id} onClick={() => setFilter(c.id)} data-testid={`sup-filter-${c.id}`}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors ${filter === c.id ? "bg-navy text-white border-navy" : "border-nardo-200 text-nardo-600 hover:bg-nardo-50"}`}>{c.label}</button>
        ))}
      </div>

      {teachers.length === 0 ? (
        <div className="py-14 text-center text-nardo-500 text-sm bg-white border border-nardo-200 rounded-xl" data-testid="sup-empty">Aún no hay publicaciones que coincidan.</div>
      ) : (
        <div className="space-y-3">
          {teachers.map((tt) => {
            const isOpen = open[tt.id] ?? true;
            return (
              <div key={tt.id} className="bg-white border border-nardo-200 rounded-xl overflow-hidden" data-testid={`sup-teacher-${tt.id}`}>
                <button onClick={() => setOpen((o) => ({ ...o, [tt.id]: !isOpen }))} data-testid={`sup-teacher-toggle-${tt.id}`}
                  className="w-full flex items-center gap-3 px-4 py-3 hover:bg-nardo-50/60 transition-colors text-left">
                  {tt.photo_url ? <img src={`${API}${tt.photo_url}`} alt="" className="w-11 h-11 rounded-full object-cover border border-nardo-100 shrink-0" /> : <SupInitials name={tt.name} />}
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-navy truncate">{tt.name}</div>
                    <div className="text-[11px] text-nardo-500 truncate">{tt.role_label}</div>
                  </div>
                  <div className="hidden sm:flex items-center gap-1.5">
                    {["aula", "recurso", "club", "enfermeria"].filter((kk) => tt.counts?.[kk]).map((kk) => (
                      <span key={kk} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${SUP_KIND[kk].cls}`}>{SUP_KIND[kk].label} {tt.counts[kk]}</span>
                    ))}
                  </div>
                  <span className="text-[11px] text-nardo-400 ml-1">{tt.items.length}</span>
                  <ChevronDown className={`w-4 h-4 text-nardo-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                  <div className="divide-y divide-nardo-100 border-t border-nardo-100">
                    {tt.items.map((it) => {
                      const k = SUP_KIND[it.kind] || SUP_KIND.aula;
                      return (
                        <div key={`${it.kind}-${it.id}`} className="px-4 py-3 flex items-center gap-3" data-testid={`sup-item-${it.id}`}>
                          <span className={`shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full ${k.cls}`}>{it.type_label}</span>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm text-navy font-medium truncate">{it.title}</div>
                            <div className="text-[11px] text-nardo-400 truncate">{it.group_label}{it.subject ? ` · ${it.subject}` : ""} · Publicado: {fmtDate(it.created_at)}{(it.attachments || []).length ? ` · ${it.attachments.length} adjunto(s)` : ""}</div>
                          </div>
                          <button onClick={() => setDetail(it)} data-testid={`sup-view-${it.id}`} className="shrink-0 inline-flex items-center gap-1 text-[11px] text-navy border border-navy/30 hover:bg-navy hover:text-white rounded-lg px-2.5 py-1.5 transition-colors"><Eye className="w-3.5 h-3.5" /> Ver</button>
                          {it.can_delete && (
                            <button onClick={() => del(it)} data-testid={`sup-delete-${it.id}`} title="Eliminar" className="shrink-0 inline-flex items-center justify-center w-8 h-8 text-red-500 border border-red-200 hover:bg-red-600 hover:text-white rounded-lg transition-colors"><Trash2 className="w-4 h-4" /></button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="pr-6 text-navy">{detail?.title}</DialogTitle></DialogHeader>
          {detail && <SupItemDetail item={detail} onDelete={del} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ---------------- Planeaciones: revisión y visto bueno de dirección ---------------- */
function PlaneacionesRevisar() {
  const { user, checkAuth } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [membreteOpen, setMembreteOpen] = useState(false);
  const [subt, setSubt] = useState(user?.pdf_brand?.planeacion_subtitle || "");
  const [savingSubt, setSavingSubt] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const canEditBrand = user?.role === "owner" || user?.role === "member";
  const load = () => { setLoading(true); api.get("/direccion/planeaciones").then((r) => setItems(r.data || [])).catch(() => setItems([])).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const repairFormat = async () => {
    if (!window.confirm("Se revisarán todas las planeaciones y se re-separará el texto que quedó 'de corrido' (días y actividades). ¿Continuar?")) return;
    setRepairing(true);
    try {
      const r = await api.post("/direccion/planeaciones/reparar-formato");
      const d = r.data || {};
      toast.success(d.reparadas > 0
        ? `Listo: se repararon ${d.reparadas} planeación(es) (${d.campos_reparados} campo(s)).`
        : "Revisado. No se encontraron planeaciones con texto de corrido.");
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo reparar"); }
    finally { setRepairing(false); }
  };

  const saveSubt = async () => {
    setSavingSubt(true);
    try {
      const pb = { ...(user?.pdf_brand || {}), planeacion_subtitle: subt.trim() };
      await api.put("/auth/profile", { pdf_brand: pb });
      await checkAuth?.();
      toast.success("Membrete de planeaciones actualizado. Se aplicará a los PDF de planeaciones.");
      setMembreteOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    finally { setSavingSubt(false); }
  };

  const review = async (p, status) => {
    let note = "";
    if (status === "cambios") { note = window.prompt("¿Qué cambios solicitas al docente? (opcional)") || ""; }
    setBusy(p.id);
    try { await api.post(`/direccion/planeaciones/${p.id}/review`, { status, note }); toast.success(status === "aprobada" ? "Planeación aprobada" : "Cambios solicitados"); load(); }
    catch { toast.error("No se pudo guardar"); }
    finally { setBusy(""); }
  };
  const viewPdf = async (p) => {
    const tid = toast.loading("Generando PDF…");
    try {
      await downloadPdfFile(`/direccion/planeaciones/${p.id}/pdf`, { filename: `planeacion_${p.proyecto}.pdf` });
      toast.success("PDF listo", { id: tid });
    } catch (e) {
      toast.error(e?.message || "No se pudo descargar el PDF. Intenta de nuevo.", { id: tid });
    }
  };

  const [teacher, setTeacher] = useState("all");
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("all");
  const [pendLim, setPendLim] = useState(5);
  const [doneLim, setDoneLim] = useState(5);
  const [tLim, setTLim] = useState(8);
  const [tq, setTq] = useState("");
  const tKey = (p) => p.docente_user_id || p.docente_name || "—";
  const teachers = Object.values(items.reduce((acc, p) => {
    const k = tKey(p);
    acc[k] = acc[k] || { key: k, name: p.docente_name || "Docente", total: 0, pend: 0 };
    acc[k].total += 1; if (p.review_status === "enviada") acc[k].pend += 1;
    return acc;
  }, {})).sort((a, b) => b.pend - a.pend || a.name.localeCompare(b.name));
  const norm = (s) => (s || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const nq = norm(q.trim());
  const shown = items.filter((p) =>
    (teacher === "all" || tKey(p) === teacher) &&
    (statusF === "all" || (statusF === "pend" ? p.review_status === "enviada" : p.review_status !== "enviada")) &&
    (!nq || norm(`${p.proyecto} ${p.docente_name} ${p.tipo} ${p.grade || ""} ${p.group_label || ""} ${p.campo_formativo || ""} ${p.formato || ""}`).includes(nq)));
  const pend = shown.filter((p) => p.review_status === "enviada");
  const done = shown.filter((p) => p.review_status !== "enviada");
  const chip = (on) => `shrink-0 text-[12px] px-3 py-1.5 rounded-full border transition-colors inline-flex items-center gap-1.5 ${on ? "bg-navy text-white border-navy" : "border-nardo-200 text-navy hover:bg-nardo-50"}`;

  const Row = ({ p }) => {
    const accent = p.color || "#0b2a5b";
    const tipoLabel = p.tipo === "preescolar" ? "Preescolar" : p.tipo === "primsec" ? "Primaria y Secundaria" : "Proyectos (NEM)";
    return (
      <div className="border border-nardo-200 rounded-lg p-4 flex flex-wrap items-center gap-3" data-testid={`dir-plan-${p.id}`}>
        <span className="w-1.5 h-10 rounded-full" style={{ background: accent }} />
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-navy text-sm">{p.proyecto}</div>
          <div className="text-[11px] text-nardo-500">{p.docente_name || "Docente"} · {tipoLabel}
            {p.review_status === "aprobada" && <span className="ml-2 text-emerald-600 font-medium">Aprobada ✔</span>}
            {p.review_status === "cambios" && <span className="ml-2 text-amber-600 font-medium">Cambios solicitados</span>}
          </div>
          {p.reviewed_by_name && p.review_status !== "enviada" && (
            <div className="text-[11px] text-indigo-600 mt-0.5" data-testid={`dir-plan-reviewer-${p.id}`}>Revisó: {p.reviewed_by_name}{p.reviewed_by_role ? ` — ${p.reviewed_by_role}` : ""}</div>
          )}
        </div>
        <button onClick={() => viewPdf(p)} data-testid={`dir-plan-pdf-${p.id}`} className="text-[12px] inline-flex items-center gap-1 border border-navy/30 text-navy hover:bg-navy hover:text-white px-3 py-1.5 rounded-lg transition-colors"><FileCheck2 className="w-3.5 h-3.5" /> Ver PDF</button>
        {p.review_status === "enviada" && (<>
          <button onClick={() => review(p, "aprobada")} disabled={busy === p.id} data-testid={`dir-plan-approve-${p.id}`} className="text-[12px] inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg font-semibold disabled:opacity-50">Aprobar</button>
          <button onClick={() => review(p, "cambios")} disabled={busy === p.id} data-testid={`dir-plan-changes-${p.id}`} className="text-[12px] inline-flex items-center gap-1 border border-amber-400 text-amber-700 hover:bg-amber-50 px-3 py-1.5 rounded-lg font-semibold">Solicitar cambios</button>
        </>)}
      </div>
    );
  };

  return (
    <Card testid="direccion-planeaciones" accent="bg-navy">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
        <h2 className="font-display font-bold text-lg text-navy flex items-center gap-2"><FileCheck2 className="w-5 h-5" /> Planeaciones para revisión</h2>
        {canEditBrand && (
          <div className="flex items-center gap-2">
            <button onClick={repairFormat} disabled={repairing} data-testid="plan-repair-format-btn"
              className="text-[12px] inline-flex items-center gap-1.5 border border-amber-400 text-amber-700 hover:bg-amber-50 px-3 py-1.5 rounded-lg font-semibold transition-colors disabled:opacity-60">
              {repairing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileCheck2 className="w-3.5 h-3.5" />} Reparar formato
            </button>
            <button onClick={() => { setSubt(user?.pdf_brand?.planeacion_subtitle || ""); setMembreteOpen(true); }} data-testid="plan-edit-membrete-btn"
              className="text-[12px] inline-flex items-center gap-1.5 border border-navy/30 text-navy hover:bg-navy hover:text-white px-3 py-1.5 rounded-lg font-semibold transition-colors">
              <FileText className="w-3.5 h-3.5" /> Editar membrete
            </button>
          </div>
        )}
      </div>
      <p className="text-xs text-nardo-500 mb-4">Revisa las planeaciones que los docentes enviaron y da tu visto bueno o solicita cambios.</p>
      {loading ? <p className="text-sm text-nardo-500">Cargando…</p> : items.length === 0 ? (
        <p className="text-sm text-nardo-500" data-testid="dir-plan-empty">Aún no hay planeaciones enviadas a dirección.</p>
      ) : (
        <div className="space-y-4">
          <div className="space-y-3" data-testid="plan-filters">
            <div className="flex flex-wrap gap-2">
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar planeación, docente, grado o materia…" data-testid="plan-search"
                className="flex-1 min-w-[220px] border border-nardo-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-navy" />
              <select value={statusF} onChange={(e) => setStatusF(e.target.value)} data-testid="plan-status-filter" className="border border-nardo-200 rounded-lg px-3 py-2 text-sm text-navy">
                <option value="all">Todas</option><option value="pend">Pendientes</option><option value="done">Revisadas</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Maestros ({teachers.length})</span>
              <input value={tq} onChange={(e) => { setTq(e.target.value); setTLim(8); }} placeholder="Buscar maestro…" data-testid="plan-teacher-search" className="ml-auto w-40 sm:w-56 border border-nardo-200 rounded-lg px-3 py-1.5 text-xs outline-none focus:border-navy" />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5" data-testid="plan-teacher-list">
              {[{ key: "all", name: "Todos los maestros", total: items.length, pend: items.filter((p) => p.review_status === "enviada").length }, ...teachers.filter((t) => !tq || norm(t.name).includes(norm(tq)))].slice(0, tLim + 1).map((t) => {
                const on = teacher === t.key;
                const ini = t.key === "all" ? "★" : t.name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
                return (
                  <button key={t.key} onClick={() => { setTeacher(t.key); setPendLim(5); setDoneLim(5); }} data-testid={`plan-teacher-${t.key}`}
                    className={`relative text-left rounded-2xl border p-3 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg ${on ? "bg-gradient-to-br from-navy-light to-navy text-white border-transparent shadow-lg shadow-navy/25" : "bg-white border-nardo-200 text-navy"}`}>
                    <div className="flex items-center gap-2.5">
                      <span className={`w-9 h-9 shrink-0 rounded-xl flex items-center justify-center text-xs font-bold ${on ? "bg-white/20" : "bg-navy/10 text-navy"}`}>{ini}</span>
                      <div className="min-w-0"><div className="text-[13px] font-semibold leading-tight line-clamp-2">{t.name}</div>
                        <div className={`text-[11px] mt-0.5 ${on ? "text-white/80" : "text-nardo-500"}`}>{t.total} planeación{t.total === 1 ? "" : "es"}</div></div>
                    </div>
                    {t.pend > 0 && <span className="absolute -top-1.5 -right-1.5 min-w-[22px] h-[22px] px-1.5 rounded-full bg-amber-500 text-white text-[11px] font-bold flex items-center justify-center border-2 border-white" title="Pendientes">{t.pend}</span>}
                  </button>
                );
              })}
            </div>
            {teachers.filter((t) => !tq || norm(t.name).includes(norm(tq))).length > tLim && <button onClick={() => setTLim((n) => n + 8)} data-testid="plan-teacher-more" className="text-xs font-semibold text-navy hover:underline">Ver más maestros</button>}
          </div>
          {shown.length === 0 && <p className="text-sm text-nardo-500" data-testid="plan-no-results">No hay planeaciones con ese filtro.</p>}
          {pend.length > 0 && <div><div className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 mb-2">Pendientes ({pend.length})</div><div className="space-y-2">{pend.slice(0, pendLim).map((p) => <Row key={p.id} p={p} />)}</div>
            {pend.length > pendLim && <button onClick={() => setPendLim((n) => n + 10)} data-testid="plan-pend-more" className="mt-2 w-full text-sm font-semibold text-navy border border-dashed border-navy/30 rounded-xl py-2 hover:bg-navy/5">Ver más ({pend.length - pendLim})</button>}</div>}
          {done.length > 0 && <div><div className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 mb-2">Revisadas ({done.length})</div><div className="space-y-2">{done.slice(0, doneLim).map((p) => <Row key={p.id} p={p} />)}</div>
            {done.length > doneLim && <button onClick={() => setDoneLim((n) => n + 10)} data-testid="plan-done-more" className="mt-2 w-full text-sm font-semibold text-navy border border-dashed border-navy/30 rounded-xl py-2 hover:bg-navy/5">Ver más ({done.length - doneLim})</button>}</div>}
        </div>
      )}

      <Dialog open={membreteOpen} onOpenChange={setMembreteOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-navy">Membrete de planeaciones</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-nardo-500">Subtítulo que aparece bajo el nombre del colegio en el PDF de las planeaciones (formato preescolar y NEM). Déjalo vacío para usar el texto por defecto.</p>
            <input type="text" value={subt} onChange={(e) => setSubt(e.target.value)} maxLength={120}
              placeholder="Planeación Didáctica · Educación Inicial y Preescolar" data-testid="plan-membrete-input"
              className="w-full border border-nardo-200 rounded-md px-3 py-2.5 text-sm" />
            <p className="text-[11px] text-nardo-400">El título grande se edita en Ajustes → Formato de PDF → “Nombre en los PDF”.</p>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setMembreteOpen(false)} className="text-sm px-3 py-2 rounded-lg border border-nardo-200 text-nardo-600 hover:bg-nardo-50">Cancelar</button>
              <button onClick={saveSubt} disabled={savingSubt} data-testid="plan-membrete-save" className="text-sm px-4 py-2 rounded-lg bg-navy text-white font-semibold hover:bg-navy-light disabled:opacity-60 inline-flex items-center gap-1.5">
                {savingSubt && <Loader2 className="w-4 h-4 animate-spin" />} Guardar
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


const TeamDirectory = () => {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  useEffect(() => {
    api.get("/direccion/team-directory").then((r) => setTeam(r.data?.team || [])).catch(() => setTeam([])).finally(() => setLoading(false));
  }, []);
  const groups = [
    { key: "direccion", label: "Dirección", tint: "bg-navy" },
    { key: "coordinacion", label: "Coordinación", tint: "bg-indigo-600" },
    { key: "administracion", label: "Administración", tint: "bg-slate-600" },
    { key: "docente", label: "Docentes", tint: "bg-emerald-600" },
  ];
  const norm = (s) => (s || "").toLowerCase();
  const filtered = team.filter((m) => !q.trim() || norm(m.name).includes(norm(q)) || norm(m.role_label).includes(norm(q)) || norm(m.email).includes(norm(q)));
  const initials = (n) => (n || "?").trim().slice(0, 1).toUpperCase();

  const PersonCard = ({ m }) => (
    <div className="bg-white border border-nardo-200 rounded-xl p-4 flex items-center gap-3.5 hover:shadow-md transition-shadow" data-testid={`team-member-${m.id}`}>
      {m.photo_url ? (
        <img src={`${API}${m.photo_url}`} alt="" className="w-14 h-14 rounded-full object-cover border-2 border-nardo-100 shrink-0" />
      ) : (
        <span className="w-14 h-14 rounded-full flex items-center justify-center bg-nardo-100 text-navy font-display font-bold text-lg border-2 border-nardo-100 shrink-0">{initials(m.name)}</span>
      )}
      <div className="min-w-0 flex-1">
        <div className="font-semibold text-navy truncate" data-testid={`team-name-${m.id}`}>{m.name}</div>
        <div className="text-[12px] text-indigo-600 font-medium truncate">{m.role_label}</div>
        <div className="flex items-center gap-3 mt-1.5">
          {m.email && <a href={`mailto:${m.email}`} className="text-[11px] text-nardo-500 hover:text-navy inline-flex items-center gap-1 truncate" title={m.email} data-testid={`team-email-${m.id}`}><Mail className="w-3 h-3 shrink-0" /> <span className="truncate max-w-[150px]">{m.email}</span></a>}
          {m.whatsapp && <a href={`https://wa.me/${m.whatsapp}`} target="_blank" rel="noreferrer" className="text-[11px] text-emerald-600 hover:text-emerald-700 inline-flex items-center gap-1" data-testid={`team-wa-${m.id}`}><MessageCircle className="w-3 h-3" /> WhatsApp</a>}
        </div>
      </div>
    </div>
  );

  return (
    <Card testid="direccion-directorio" accent="bg-indigo-600">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h2 className="font-display font-semibold text-xl text-navy flex items-center gap-2"><Users className="w-5 h-5 text-indigo-600" /> Directorio del equipo</h2>
        <div className="relative">
          <Search className="w-4 h-4 text-nardo-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre, rol o correo" data-testid="team-search" className="pl-8 pr-3 py-2 text-sm border border-nardo-200 rounded-lg outline-none focus:border-indigo-400 w-64 max-w-full" />
        </div>
      </div>
      <p className="text-xs text-nardo-500 mb-5">Fotos, áreas y contactos de todo el personal del colegio.</p>
      {loading ? (
        <p className="text-sm text-nardo-400" data-testid="team-loading">Cargando directorio…</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-nardo-400" data-testid="team-empty">No se encontró personal con ese criterio.</p>
      ) : (
        <div className="space-y-6">
          {groups.map((g) => {
            const members = filtered.filter((m) => m.kind === g.key);
            if (!members.length) return null;
            return (
              <div key={g.key} data-testid={`team-group-${g.key}`}>
                <div className="flex items-center gap-2 mb-2.5">
                  <span className={`w-2 h-2 rounded-full ${g.tint}`} />
                  <h3 className="text-sm font-semibold text-navy">{g.label} <span className="text-nardo-400 font-normal">({members.length})</span></h3>
                </div>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {members.map((m) => <PersonCard key={m.id} m={m} />)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

const CoordProfileCard = () => {
  const { user, checkAuth } = useAuth();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const areaWord = user?.title === "coordinadora" ? "Coordinadora de" : "Coordinador de";
  const onPick = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      await api.post("/coord/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await checkAuth();
      toast.success("Foto de perfil actualizada");
    } catch { toast.error("No se pudo subir la foto"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  return (
    <div className="flex items-center gap-4 bg-white border border-nardo-200 rounded-2xl p-4 mb-6 shadow-sm" data-testid="coord-profile-card">
      <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} className="relative group shrink-0 disabled:opacity-60" data-testid="coord-photo-edit" title="Cambiar foto de perfil">
        {user?.photo_url ? (
          <img src={`${API}${user.photo_url}`} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-indigo-200" />
        ) : (
          <span className="w-16 h-16 rounded-full flex items-center justify-center bg-indigo-100 text-indigo-600 text-xl font-bold border-2 border-indigo-200">{(user?.name || "C").slice(0, 1).toUpperCase()}</span>
        )}
        <span className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center border-2 border-white group-hover:bg-indigo-700 transition-colors"><Camera className="w-3 h-3" /></span>
      </button>
      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPick} data-testid="coord-photo-input" />
      <div className="min-w-0">
        <div className="font-display font-bold text-lg text-navy truncate" data-testid="coord-name">{user?.name || "Coordinación"}</div>
        <div className="text-sm text-indigo-700 font-medium" data-testid="coord-area">{areaWord}{user?.area ? ` ${user.area}` : " —"}</div>
        <div className="text-[11px] text-nardo-400 truncate">{user?.email}</div>
      </div>
    </div>
  );
};

export default function Direccion() {
  const { user } = useAuth();
  const isStaff = user?.role === "staff";
  const isCoord = isStaff && user?.staff_kind === "coordinacion";
  const allTabs = [...TABS, ...ADMIN_EXTRA_TABS];
  const visibleTabs = isCoord ? COORD_TABS : (isStaff ? STAFF_TAB_IDS.map((id) => allTabs.find((t) => t.id === id)).filter(Boolean) : TABS);
  const [tab, setTabRaw] = useState(() => sessionStorage.getItem("zv_tab_direccion") || (isCoord ? "asistencias" : "tablero"));
  const setTab = (t) => { try { sessionStorage.setItem("zv_tab_direccion", t); } catch { /* noop */ } setTabRaw(t); };
  const [people, setPeople] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const loadPendingCount = async () => { try { const r = await api.get("/payments/pending"); setPendingCount((r.data || []).length); } catch {} };
  useEffect(() => {
    (async () => {
      try {
        const [s, t] = await Promise.all([api.get("/students", { params: { kind: "student" } }), api.get("/students", { params: { kind: "teacher" } })]);
        setPeople([...s.data, ...t.data]);
      } catch {}
    })();
    if (!isCoord && hasModule(user, "cobranza")) loadPendingCount();
  }, [isCoord]);
  const tabMod = TAB_MODULE[tab];
  const tabLocked = !hasModule(user, tabMod);
  const coordTitleWord = user?.title === "coordinadora" ? "Coordinadora" : "Coordinador";
  return (
    <div className="max-w-7xl mx-auto" data-testid="direccion-page">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.3em] text-navy-light mb-3 flex items-center gap-2">
          <span className="w-8 h-px bg-navy" /> {isCoord ? `Portal de coordinación · ${coordTitleWord}` : (isStaff ? "Panel administrativo" : "Centro de control")}
        </div>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-navy flex items-center gap-3"><Building2 className="w-8 h-8 text-navy-light" strokeWidth={1.5} /> {isCoord ? "Coordinación" : (isStaff ? "Área administrativa" : "Dirección")}</h1>
      </div>

      {isCoord && <CoordProfileCard />}
      {isCoord && user?.area === "Inglés" && (
        <div className="mb-6 max-w-md" data-testid="coord-signature-block">
          <SignatureUploader title="Mi firma de coordinación de Inglés" help="Sube tu firma escaneada (PNG con fondo transparente). Aparecerá en el PDF de las planeaciones de Inglés de los docentes, sobre la línea 'Coordination'." />
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-6 border-b border-nardo-200 pb-2">
        {visibleTabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            data-testid={`direccion-tab-${id}`}
            onClick={() => setTab(id)}
            className={`relative inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === id ? "bg-navy text-white" : "text-nardo-600 hover:text-navy hover:bg-nardo-50"}`}
          >
            <Icon className="w-4 h-4" /> {label}
            {!hasModule(user, TAB_MODULE[id]) && <Lock className="w-3.5 h-3.5 opacity-60" data-testid={`direccion-tab-${id}-locked`} />}
            {id === "recibos" && pendingCount > 0 && (
              <span data-testid="recibos-pending-badge" className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full bg-red-500 text-white">{pendingCount}</span>
            )}
          </button>
        ))}
      </div>

      {tabLocked ? <ModuleLocked moduleKey={tabMod} /> : (<>
      {tab === "tablero" && <Tablero />}
      {tab === "aula" && <AulaAdmin />}
      {tab === "aula-super" && <AulaSupervision />}
      {tab === "biblioteca" && <BibliotecaAdmin />}
      {tab === "clubs" && <ClubsAdmin />}
      {tab === "comunicados" && <Comunicados />}
      {tab === "documentos" && <Documentos />}
      {tab === "calendario" && <Calendario />}
      {tab === "justificantes" && <Justificantes people={people} />}
      {tab === "credenciales" && <Credenciales people={people} />}
      {tab === "recibos" && <Recibos onPendingChange={loadPendingCount} />}
      {tab === "gastos" && <GastosInternos />}
      {tab === "docentes" && <Docentes />}
      {tab === "incidencias" && <IncidenciasDireccion />}
      {tab === "horarios" && <Horarios />}
      {tab === "jornadas" && <JornadasAdmin />}
      {tab === "comedor" && <Comedor />}
      {tab === "asistencias" && (
        <div className="space-y-6">
          <AttendanceStatusPanel />
          {isCoord && <GroupAttendanceDownload title="Descargar asistencia por grupo (Día / Semana / Mes / Año)" />}
        </div>
      )}
      {tab === "calificaciones" && <Boletas />}
      {tab === "planeaciones" && <PlaneacionesRevisar />}
      {tab === "califdocentes" && <CalificacionesDocentes />}
      {tab === "admisiones" && <Admisiones />}
      {tab === "bajas" && <HistorialBajas />}
      {tab === "directorio" && <TeamDirectory />}
      {tab === "dosificacion" && <DosificacionOverview />}
      {tab === "musica" && <MusicModule />}
      {tab === "recursos" && <CoordRecursos />}
      {tab === "club-comunicados" && <ClubComunicadosCoord />}
      </>)}
    </div>
  );
}

function CoordRecursos() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [subject, setSubject] = useState("");
  const [showForm, setShowForm] = useState(false);
  const load = () => {
    setData(null);
    const p = {}; if (q.trim()) p.q = q.trim(); if (subject) p.subject = subject;
    api.get("/direccion/teacher-resources", { params: p }).then((r) => setData(r.data)).catch(() => setData({ resources: [], subjects: [] }));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [subject]);
  const openFile = async (url) => {
    try { const r = await api.get(url, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); }
    catch { toast.error("No se pudo abrir el recurso"); }
  };
  return (
    <div data-testid="coord-recursos-panel">
      <h2 className="font-display font-bold text-lg text-navy mb-1 flex items-center gap-2"><Library className="w-5 h-5 text-navy-light" /> Banco de recursos</h2>
      <p className="text-xs text-nardo-500 mb-4">Sube documentos, imágenes, audio o video y compártelos con toda la comunidad: docentes, administración y dirección.</p>
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[200px]"><Search className="w-4 h-4 text-nardo-400 absolute left-2.5 top-2.5" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Buscar recurso y Enter" data-testid="coord-rec-search" className="w-full border border-nardo-200 rounded-lg pl-8 pr-3 py-2 text-sm text-navy outline-none focus:border-navy" /></div>
        <select value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="coord-rec-subject" className="border border-nardo-200 rounded-lg px-3 py-2 text-sm text-navy bg-white outline-none focus:border-navy">
          <option value="">Todas las materias</option>
          {(data?.subjects || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={() => setShowForm(true)} data-testid="coord-rec-add-btn" className="text-sm inline-flex items-center gap-1.5 bg-navy hover:bg-navy-light text-white px-4 py-2 rounded-lg font-semibold"><Plus className="w-4 h-4" /> Subir recurso</button>
      </div>
      {data === null ? <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-navy" /></div>
        : data.resources.length === 0 ? <div className="py-16 text-center text-nardo-500" data-testid="coord-rec-empty">No hay recursos que coincidan.</div>
        : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="coord-rec-grid">
            {data.resources.map((r) => (
              <div key={r.id} className="bg-white border border-nardo-200 rounded-xl p-3.5" data-testid={`coord-rec-${r.id}`}>
                <div className="flex items-start gap-2">
                  <span className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center ${r.kind === "file" ? "bg-sky-100 text-sky-700" : "bg-emerald-100 text-emerald-700"}`}>{r.kind === "file" ? <FileText className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}</span>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm text-navy truncate">{r.title}</div>
                    <div className="text-[11px] text-nardo-500">{r.subject || "General"} · {r.grade_label}</div>
                  </div>
                </div>
                {r.description && <p className="text-xs text-nardo-600 mt-2 line-clamp-2">{r.description}</p>}
                <div className="flex items-center justify-between mt-2 text-[11px] text-nardo-400">
                  <span>{r.docente_name || "Docente"}</span>
                  {r.kind === "file" && r.file_url
                    ? <button onClick={() => openFile(r.file_url)} className="inline-flex items-center gap-1 text-navy hover:underline font-semibold"><FileDown className="w-3.5 h-3.5" /> Abrir</button>
                    : r.url ? <a href={r.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-navy hover:underline font-semibold"><ExternalLink className="w-3.5 h-3.5" /> Abrir</a> : null}
                </div>
              </div>
            ))}
          </div>
        )}
      {showForm && <CoordResourceForm grades={data?.grades || []} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function CoordResourceForm({ grades, onClose, onSaved }) {
  const [tab, setTab] = useState("file");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [subject, setSubject] = useState("");
  const [grade, setGrade] = useState("");
  const [url, setUrl] = useState("");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const groupLabel = (v) => { const s = String(v || ""); return s.includes("|") ? s.split("|").join(" · ") : s; };

  const submit = async () => {
    if (!title.trim()) { toast.error("El título es obligatorio"); return; }
    if (tab === "file" && !file) { toast.error("Selecciona un archivo"); return; }
    if (tab === "link" && !url.trim()) { toast.error("Escribe un enlace"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", title.trim());
      fd.append("description", description.trim());
      fd.append("subject", subject.trim());
      fd.append("grade", grade);
      fd.append("visibility", "colegio");
      if (tab === "file") fd.append("file", file); else fd.append("url", url.trim());
      await api.post("/direccion/teacher-resources", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Recurso agregado");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-navy-ink/70 flex items-start justify-center overflow-y-auto p-4" onClick={onClose} data-testid="coord-res-form-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-nardo-200">
          <div className="font-display font-bold text-navy">Nuevo recurso</div>
          <button onClick={onClose} className="text-nardo-500 hover:text-navy p-1"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex gap-2">
            <button onClick={() => setTab("file")} data-testid="coord-res-tab-file" className={`flex-1 text-xs font-semibold py-2 rounded-lg inline-flex items-center justify-center gap-1.5 ${tab === "file" ? "bg-navy text-white" : "bg-nardo-100 text-nardo-600"}`}><Paperclip className="w-4 h-4" /> Archivo</button>
            <button onClick={() => setTab("link")} data-testid="coord-res-tab-link" className={`flex-1 text-xs font-semibold py-2 rounded-lg inline-flex items-center justify-center gap-1.5 ${tab === "link" ? "bg-navy text-white" : "bg-nardo-100 text-nardo-600"}`}><Link2 className="w-4 h-4" /> Enlace</button>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 block mb-1">Título *</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="coord-res-title" className="w-full border border-nardo-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-navy" placeholder="Ej. Guía de lectura" />
          </div>
          {tab === "file" ? (
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 block mb-1">Archivo (documento, imagen, audio o video) — máx 50 MB</label>
              <input type="file" accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,image/*,audio/*,video/*" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="coord-res-file" className="w-full text-xs" />
              <p className="text-[10px] text-nardo-400 mt-1">¿Video largo o pesado? Compártelo con la pestaña <b>Enlace</b> (YouTube, Drive…).</p>
            </div>
          ) : (
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 block mb-1">Enlace (YouTube, Drive, web…)</label>
              <input value={url} onChange={(e) => setUrl(e.target.value)} data-testid="coord-res-url" className="w-full border border-nardo-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-navy" placeholder="https://…" />
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 block mb-1">Descripción</label>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} data-testid="coord-res-desc" rows={2} className="w-full border border-nardo-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-navy resize-none" placeholder="Opcional" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 block mb-1">Materia</label>
              <input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="coord-res-subject" className="w-full border border-nardo-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-navy" placeholder="Materia" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 block mb-1">Grupo</label>
              <select value={grade} onChange={(e) => setGrade(e.target.value)} data-testid="coord-res-grade" className="w-full border border-nardo-200 rounded-lg px-3 py-2 text-sm bg-white outline-none focus:border-navy">
                <option value="">Todos los grados</option>
                {grades.map((g) => {
                  const val = typeof g === "string" ? g : (g.value || g.grade || "");
                  const lbl = typeof g === "string" ? groupLabel(g) : (g.label || groupLabel(val));
                  return <option key={val} value={val}>{lbl}</option>;
                })}
              </select>
            </div>
          </div>
          <button onClick={submit} disabled={saving} data-testid="coord-res-submit" className="w-full text-sm inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-light text-white py-2.5 rounded-lg font-semibold disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Guardar recurso
          </button>
        </div>
      </div>
    </div>
  );
}


/* ---------------- Comunicados de Clubs — supervisión de Coordinación/Dirección ---------------- */
function ClubComunicadosCoord() {
  const [data, setData] = useState(null);
  const [clubFilter, setClubFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setData(null);
    const p = {}; if (clubFilter) p.club_id = clubFilter;
    api.get("/direccion/club-announcements", { params: p })
      .then((r) => setData(r.data)).catch(() => setData({ announcements: [], clubs: [] }));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clubFilter]);

  const del = async (a) => {
    if (!window.confirm("¿Eliminar este comunicado?")) return;
    try { await api.delete(`/direccion/club-announcements/${a.id}`); toast.success("Comunicado eliminado"); load(); }
    catch { toast.error("No se pudo eliminar"); }
  };

  return (
    <div data-testid="coord-club-comunicados">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div>
          <h2 className="font-display font-bold text-lg text-navy flex items-center gap-2"><Megaphone className="w-5 h-5 text-navy-light" /> Comunicados de clubs</h2>
          <p className="text-xs text-nardo-500">Supervisa lo que envían los docentes de clubs a los papás. Puedes publicar, editar o eliminar.</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <select value={clubFilter} onChange={(e) => setClubFilter(e.target.value)} data-testid="coord-club-filter" className="border border-nardo-200 rounded-lg px-3 py-2 text-sm text-navy bg-white outline-none focus:border-navy">
            <option value="">Todos los clubs</option>
            {(data?.clubs || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="coord-club-add-btn" className="text-sm inline-flex items-center gap-1.5 bg-navy hover:bg-navy-light text-white px-4 py-2 rounded-lg font-semibold"><Plus className="w-4 h-4" /> Publicar</button>
        </div>
      </div>
      {data === null ? <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin inline text-navy" /></div>
        : data.announcements.length === 0 ? <div className="py-16 text-center text-nardo-500" data-testid="coord-club-empty">Aún no hay comunicados de clubs.</div>
        : (
          <div className="space-y-3">
            {data.announcements.map((a) => (
              <div key={a.id} className="bg-white border border-nardo-200 rounded-xl p-4" data-testid={`coord-club-ann-${a.id}`}>
                <div className="flex items-start gap-3">
                  <span className="shrink-0 text-[10px] font-semibold text-white bg-indigo-600 px-2 py-1 rounded-full">{a.club_name || "Club"}</span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-navy">{a.title}</div>
                    {a.body && <div className="text-sm text-nardo-600 mt-0.5 whitespace-pre-wrap">{a.body}</div>}
                    {a.file_url && <a href={`${API}${a.file_url}`} target="_blank" rel="noreferrer" className="text-[11px] text-navy underline mt-1 inline-flex items-center gap-1"><Paperclip className="w-3 h-3" /> {a.file_name || "Adjunto"}</a>}
                    <div className="text-[11px] text-nardo-400 mt-1">Por {a.created_by_name} ({a.created_by_role}) · {(a.created_at || "").slice(0, 10)}</div>
                  </div>
                  <div className="flex flex-col gap-1.5 shrink-0">
                    <button onClick={() => { setEditing(a); setShowForm(true); }} data-testid={`coord-club-edit-${a.id}`} className="text-[11px] inline-flex items-center gap-1 text-navy border border-navy/30 hover:bg-navy hover:text-white rounded-lg px-2.5 py-1.5 transition-colors"><Pencil className="w-3.5 h-3.5" /> Editar</button>
                    <button onClick={() => del(a)} data-testid={`coord-club-del-${a.id}`} className="text-[11px] inline-flex items-center gap-1 text-red-500 hover:bg-red-50 rounded-lg px-2.5 py-1.5"><Trash2 className="w-3.5 h-3.5" /> Eliminar</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      {showForm && <ClubComunicadoForm clubs={data?.clubs || []} editing={editing} onClose={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
    </div>
  );
}

function ClubComunicadoForm({ clubs, editing, onClose, onSaved }) {
  const [clubId, setClubId] = useState(editing?.club_id || (clubs[0]?.id || ""));
  const [title, setTitle] = useState(editing?.title || "");
  const [body, setBody] = useState(editing?.body || "");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const isEdit = !!editing;

  const submit = async () => {
    if (!title.trim()) { toast.error("El título es obligatorio"); return; }
    if (!isEdit && !clubId) { toast.error("Selecciona un club"); return; }
    setSaving(true);
    try {
      if (isEdit) {
        await api.put(`/direccion/club-announcements/${editing.id}`, { title: title.trim(), body: body.trim() });
      } else {
        const fd = new FormData();
        fd.append("club_id", clubId);
        fd.append("title", title.trim());
        fd.append("body", body.trim());
        if (file) fd.append("file", file);
        await api.post("/direccion/club-announcements", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(isEdit ? "Comunicado actualizado" : "Comunicado publicado · se notificó a los papás");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo guardar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-navy-ink/70 flex items-start justify-center overflow-y-auto p-4" onClick={onClose} data-testid="coord-club-form-modal">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full my-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-nardo-200">
          <div className="font-display font-bold text-navy">{isEdit ? "Editar comunicado" : "Nuevo comunicado de club"}</div>
          <button onClick={onClose} className="text-nardo-500 hover:text-navy p-1"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {!isEdit && (
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 block mb-1">Club *</label>
              <select value={clubId} onChange={(e) => setClubId(e.target.value)} data-testid="coord-club-form-club" className="w-full border border-nardo-200 rounded-lg px-3 py-2 text-sm bg-white outline-none focus:border-navy">
                {clubs.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.student_count} alumnos)</option>)}
              </select>
              <p className="text-[10px] text-nardo-500 mt-1">Se notificará a los papás de los alumnos de ese club.</p>
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 block mb-1">Título *</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="coord-club-form-title" className="w-full border border-nardo-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-navy" placeholder="Ej. Suspensión de ensayo" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 block mb-1">Mensaje</label>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} data-testid="coord-club-form-body" rows={4} className="w-full border border-nardo-200 rounded-lg px-3 py-2 text-sm outline-none focus:border-navy resize-none" placeholder="Escribe la información para los papás…" />
          </div>
          {!isEdit && (
            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 flex items-center gap-1.5 mb-1"><Paperclip className="w-3.5 h-3.5" /> Adjunto (opcional, máx 25 MB)</label>
              <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="coord-club-form-file" className="w-full text-xs" />
            </div>
          )}
          <button onClick={submit} disabled={saving} data-testid="coord-club-form-submit" className="w-full text-sm inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-light text-white py-2.5 rounded-lg font-semibold disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Megaphone className="w-4 h-4" />} {isEdit ? "Guardar cambios" : "Publicar y notificar"}
          </button>
        </div>
      </div>
    </div>
  );
}
